//changepage
import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";

import {
  Users,
  TrendingUp,
  TrendingDown,
  MapPin,
  AlertCircle,
  X,
  Check,
  Search,
  DollarSign,
  ArrowRight,
  History,
  BarChart3,
  Calendar,
  FileText,
  Filter,
  Download,
  Mail,
  Phone,
  Building,
  Briefcase,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  UserCheck,
  UserX,
  UserMinus,
  RefreshCw,
  GraduationCap,
  FileCheck,
  Repeat,
  ClipboardList,
  Zap,
  ChevronDown,
  Sparkles,
  Clock,
  Target,
  Loader,
  Shield,
  Upload,
  Lock,
  Bell,
  ShieldCheck,
  Code,
} from "lucide-react";
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";
import employeeActionsAPI from "./changesAPIs";
import ReusableDropdown from "../components/ReusableDropdown";
import { getEnglishInitials } from "../components/UserAvatar";
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";

// ===================================
// TRANSLATIONS - دعم عربي/إنجليزي كامل
// ===================================
const translations = {
  ar: {
    // Header
    title: "إدارة الإجراءات ",

    // Stats
    totalEmployees: "إجمالي الموظفين",
    promotionsThisMonth: "ترقيات هذا الشهر",
    transfersThisMonth: "نقل هذا الشهر",
    avgSalaryIncrease: "متوسط زيادة الراتب",
    pendingApprovals: "موافقات معلقة",
    completedActions: "إجراءات مكتملة",
    activeWorkforce: "القوى العاملة النشطة",
    careerGrowth: "النمو الوظيفي",
    departmentMoves: "تنقلات الأقسام",
    compensationGrowth: "نمو التعويض",
    awaitingReview: "في انتظار المراجعة",
    thisMonth: "هذا الشهر",
    fromYourTeam: "من فريقك",
    myTeam: "فريقي",
    selectDepartment: "اختر القسم",


    // Filters & Search
    searchFilterEmployees: "البحث وتصفية الموظفين",
    showing: "عرض",
    of: "من",
    employees: "موظف",
    forSelectedClient: "للعميل المحدد",
    viewHistory: "عرض السجل",
    refresh: "تحديث",
    searchEmployee: "البحث عن موظف",
    searchPlaceholder: "البحث بالاسم، البريد الإلكتروني، الوظيفة",
    client: "العميل",
    allClients: "جميع العملاء",
    department: "القسم",
    allDepartments: "جميع الأقسام",
    performance: "الأداء",
    allPerformanceLevels: "جميع مستويات الأداء",
    status: "الحالة",
    allStatuses: "جميع الحالات",
    sortBy: "الترتيب حسب",
    name: "الاسم",
    salary: "الراتب",
    joinDate: "تاريخ الانضمام",
    ascendingOrder: "ترتيب تصاعدي",
    descendingOrder: "ترتيب تنازلي",

    // Table
    employee: "الموظف",
    actions: "الإجراءات",
    loadingEmployees: "جاري تحميل الموظفين...",
    noEmployeesFound: "لم يتم العثور على موظفين",
    noEmployeesMatch: "لا يوجد موظفون يطابقون معايير البحث والتصفية الحالية.",
    pleaseWait: "يرجى الانتظار أثناء تحميل بيانات الموظفين...",
    clearFilters: "مسح التصفية",

    // Action Types
    selectActionType: "اختر نوع الإجراء",
    chooseAction: "اختر إجراءً للقيام به",
    promoteEmployee: "ترقية الموظف",
    demoteEmployee: "تخفيض رتبة الموظف",
    transferEmployee: "نقل الموظف",
    salaryAdjustment: "تعديل الراتب",
    terminateEmployment: "إنهاء الخدمة",
    suspendEmployee: "إيقاف الموظف",
    rehireEmployee: "إعادة توظيف الموظف",
    contractUpdate: "تحديث العقد",
    jobRotation: "التناوب الوظيفي",
    performanceReview: "تقييم الأداء",
    trainingAssignment: "تعيين تدريب",

    // Action Descriptions
    advanceToHigher: "ترقية الموظف لمنصب أعلى",
    moveToLower: "نقل الموظف لمنصب أقل",
    transferDifferent: "نقل إلى قسم مختلف",
    modifyCompensation: "تعديل تعويض الموظف",
    endEmployment: "إنهاء عقد العمل",
    temporarySuspension: "الإيقاف المؤقت عن العمل",
    reinstateFormer: "إعادة تعيين موظف سابق",
    modifyContract: "تعديل عقد العمل",
    rotateDifferent: "التناوب لدور/قسم مختلف",
    conductEvaluation: "إجراء تقييم الأداء",
    assignTraining: "تعيين برنامج تدريبي",

    // Approval Levels
    requiresHRManager: "يتطلب موافقة مدير الموارد البشرية",
    requiresDeptHead: "يتطلب موافقة رئيس القسم",
    requiresBothManagers: "يتطلب موافقة كلا المديرين",
    requiresFinanceManager: "يتطلب موافقة مدير المالية",
    requiresHRDirector: "يتطلب موافقة مدير الموارد البشرية",
    requiresLegal: "يتطلب موافقة القانوني",
    requiresDirectManager: "يتطلب موافقة المدير المباشر",
    requiresTrainingManager: "يتطلب موافقة مدير التدريب",

    // Form Fields - Common
    effectiveDate: "تاريخ السريان",
    reasonForAction: "سبب الإجراء",
    provideDetailedReason: "قدم سببًا تفصيليًا لهذا الإجراء...",
    approverNotes: "ملاحظات المعتمد",
    addAdditionalNotes: "أضف أي ملاحظات أو تعليقات إضافية...",
    approvalRequired: "الموافقة مطلوبة",
    thisActionRequires: "يتطلب هذا الإجراء موافقة من:",

    // Promote Fields
    newPosition: "المنصب الجديد",
    enterNewPosition: "أدخل عنوان المنصب الجديد...",
    newSalary: "الراتب الجديد",
    enterNewSalary: "أدخل مبلغ الراتب الجديد...",
    newDepartment: "القسم الجديد",
    keepCurrentDept: "الاحتفاظ بالقسم الحالي",
    currentSalary: "الحالي",
    newSalaryAmount: "جديد",

    // Demote Fields
    demotionReasonCategory: "فئة سبب التخفيض",
    selectReason: "اختر السبب",
    performanceIssues: "مشاكل في الأداء",
    companyRestructuring: "إعادة هيكلة الشركة",
    misconduct: "سوء السلوك",
    businessNeeds: "احتياجات العمل",
    other: "أخرى",

    // Transfer Fields
    selectDepartment: "اختر القسم",
    keepCurrentPosition: "الاحتفاظ بالمنصب الحالي أو إدخال واحد جديد...",
    newManager: "المدير الجديد",
    transferType: "نوع النقل",
    selectType: "اختر النوع",
    permanentTransfer: "نقل دائم",
    temporaryTransfer: "نقل مؤقت",
    projectBased: "قائم على المشروع",

    // Salary Change Fields
    adjustmentType: "نوع التعديل",
    annualIncrease: "زيادة سنوية",
    promotion: "ترقية",
    marketAdjustment: "تعديل السوق",
    performanceBased: "قائم على الأداء",
    costOfLiving: "تكلفة المعيشة",
    salaryCorrection: "تصحيح الراتب",

    // Terminate Fields
    terminationType: "نوع الإنهاء",
    voluntaryResignation: "استقالة طوعية",
    involuntaryTermination: "إنهاء غير طوعي",
    retirement: "التقاعد",
    contractEnd: "نهاية العقد",
    mutualAgreement: "اتفاق متبادل",
    layoff: "تسريح العمال",
    lastWorkingDay: "آخر يوم عمل",
    terminationReason: "سبب الإنهاء",
    provideDetailedTermination: "قدم سبب الإنهاء التفصيلي...",
    severancePackage: "حزمة الفصل",
    enterSeveranceAmount: "أدخل مبلغ الفصل إذا كان ذلك منطبقًا...",
    eligibleForRehire: "مؤهل لإعادة التوظيف؟",
    yes: "نعم",
    no: "لا",
    conditional: "مشروط",
    showMore: "عرض المزيد",
    remaining: "متبقي",
    // Suspend Fields
    suspensionStartDate: "تاريخ بدء الإيقاف",
    suspensionEndDate: "تاريخ انتهاء الإيقاف",
    suspensionType: "نوع الإيقاف",
    withPay: "مع الراتب",
    withoutPay: "بدون راتب",
    partialPay: "راتب جزئي",
    suspensionReason: "سبب الإيقاف",
    provideDetailedSuspension: "قدم سبب الإيقاف التفصيلي...",

    // Rehire Fields
    position: "المنصب",
    enterPositionTitle: "أدخل عنوان المنصب...",
    startingSalary: "الراتب الابتدائي",
    enterStartingSalary: "أدخل الراتب الابتدائي...",
    rehireDate: "تاريخ إعادة التوظيف",

    // Contract Fields
    contractType: "نوع العقد",
    selectContractType: "اختر نوع العقد",
    permanent: "دائم",
    fixedTerm: "محدد المدة",
    temporary: "مؤقت",
    partTime: "دوام جزئي",
    consultant: "استشاري",
    contractor: "متعاقد",
    contractStartDate: "تاريخ بداية العقد",
    contractEndDate: "تاريخ انتهاء العقد",
    contractTerms: "شروط العقد",
    enterContractTerms: "أدخل أي شروط أو أحكام خاصة بالعقد...",

    // Rotation Fields
    rotationDepartment: "قسم التناوب",
    rotationRole: "دور التناوب",
    enterRotationRole: "أدخل دور التناوب...",
    durationMonths: "المدة (أشهر)",
    enterDuration: "أدخل المدة بالأشهر...",
    rotationObjectives: "أهداف التناوب",
    enterObjectives: "أدخل أهداف وغايات التعلم...",

    // Review Fields
    performanceRating: "تقييم الأداء",
    selectRating: "اختر التقييم",
    excellent: "ممتاز",
    veryGood: "جيد جدًا",
    good: "جيد",
    average: "متوسط",
    belowAverage: "دون المتوسط",
    poor: "ضعيف",
    reviewScore: "درجة التقييم (1-10)",
    enterScore: "أدخل الدرجة...",
    strengths: "نقاط القوة",
    listStrengths: "قائمة بنقاط القوة والإنجازات للموظف...",
    areasForImprovement: "مجالات التحسين",
    listImprovements: "قائمة بالمجالات التي تحتاج إلى تحسين...",
    nextReviewDate: "تاريخ المراجعة التالي",

    // Training Fields
    trainingProgram: "برنامج التدريب",
    enterTrainingProgram: "أدخل اسم برنامج التدريب...",
    trainingStartDate: "تاريخ بدء التدريب",
    trainingEndDate: "تاريخ انتهاء التدريب",
    trainingCost: "تكلفة التدريب",
    enterTrainingCost: "أدخل تكلفة التدريب...",
    trainingObjectives: "أهداف التدريب",
    enterTrainingObjectives: "أدخل أهداف ونتائج التدريب المتوقعة...",
    selectClient: "اختر عميل...",
    selectDepartment: "اختر القسم...",
    selectPerformance: "اختر الأداء...",
    selectStatus: "اختر الحالة...",
    // Modal Actions
    selectAction: "اختر إجراءً",
    selectActionToProceed:
      "اختر نوع إجراء من القائمة المنسدلة أعلاه للمتابعة مع إدارة الموظف",
    employeeAction: "إجراء الموظف",
    employeeName: "اسم الموظف",
    infinityCode: "رقم Infinity",
    currentPosition: "المنصب الحالي",
    currentDepartment: "القسم الحالي",
    reportsTo: "يرفع تقاريره إلى",
    cancel: "إلغاء",
    submitAction: "إرسال الإجراء",

    // Confirmation Modal
    confirmAction: "تأكيد الإجراء",
    reviewBeforeConfirm: "يرجى مراجعة التفاصيل قبل تأكيد هذا الإجراء",
    actionDetails: "تفاصيل الإجراء",
    actionType: "نوع الإجراء",
    reason: "السبب",
    confirmSubmit: "تأكيد وإرسال",
    processing: "جاري المعالجة...",

    // History Modal
    actionHistory: "سجل الإجراءات",
    date: "التاريخ",
    details: "التفاصيل",
    noActionHistory: "لا يوجد سجل إجراءات",
    noActionsRecorded:
      "لم يتم تسجيل أي إجراءات موظفين بعد. ابدأ باتخاذ إجراء على موظف.",
    noDetailsProvided: "لم يتم توفير تفاصيل",

    // Pending Approvals Modal
    pendingApprovalsTitle: "الموافقات المعلقة",
    actionsAwaiting: "إجراء في انتظار الموافقة",
    requestedDate: "تاريخ الطلب",
    approvalLevel: "مستوى الموافقة",
    approve: "موافقة",
    reject: "رفض",
    noPendingApprovals: "لا توجد موافقات معلقة",
    allActionsReviewed:
      "تمت مراجعة جميع الإجراءات. لا توجد موافقات معلقة في هذا الوقت.",

    // Employee History Modal
    employeeHistoryTitle: "سجل الموظف",
    currentInfo: "المعلومات الحالية",
    actionTimeline: "الجدول الزمني للإجراءات",
    records: "سجلات",
    noHistoryFound: "لم يتم العثور على سجل",
    noActionsForEmployee: "لم يتم تسجيل أي إجراءات لهذا الموظف بعد.",

    // Statuses
    pending: "معلق",
    approved: "موافق عليه",
    rejected: "مرفوض",
    completed: "مكتمل",
    unknown: "غير معروف",

    // Messages
    successActionApproved: "تمت الموافقة على الإجراء بنجاح",
    successActionRejected: "تم رفض الإجراء بنجاح",
    errorApprovingAction: "فشل الموافقة على الإجراء. يرجى المحاولة مرة أخرى.",
    errorRejectingAction: "فشل رفض الإجراء. يرجى المحاولة مرة أخرى.",
    errorLoadingEmployees: "فشل تحميل الموظفين. يرجى المحاولة مرة أخرى.",
    pleaseSelectAction: "يرجى اختيار نوع إجراء",
    effectiveDateRequired: "تاريخ السريان مطلوب",
    reasonRequired: "سبب الإجراء مطلوب",
    approverNotesRequired: "ملاحظات المعتمد مطلوبة",
    actionSubmittedSuccess: "تم إرسال الإجراء بنجاح",
    awaitingApproval: "في انتظار الموافقة",
    salaryWillIncrease: "سيزداد الراتب بمقدار",
    salaryWillDecrease: "سينخفض الراتب بمقدار",
    changesAppliedOnApproval:
      "سيتم تطبيق التغييرات على سجل الموظف والرواتب عند الموافقة.",
    changesAppliedImmediately:
      "سيتم تطبيق التغييرات على سجل الموظف والرواتب فورًا.",
    errorProcessingAction: "خطأ في معالجة الإجراء.",
    pleaseTryAgain: "يرجى المحاولة مرة أخرى أو الاتصال بالدعم.",

    // Misc
    notAvailable: "غير متوفر",
    egp: "ج.م",
    searchForManagers: "البحث عن المدراء...",
    noManagersFound: "لم يتم العثور على مدراء",
    tryDifferentSearch: "جرب مصطلح بحث مختلف",
    noManagersAvailable: "لا يوجد مدراء متاحون",
  },
  en: {
    // Header
    title: "Employee Actions & Management",

    // Stats
    totalEmployees: "Total Employees",
    promotionsThisMonth: "Promotions This Month",
    transfersThisMonth: "Transfers This Month",
    avgSalaryIncrease: "Avg. Salary Increase",
    pendingApprovals: "Pending Approvals",
    completedActions: "Completed Actions",
    activeWorkforce: "Active workforce",
    careerGrowth: "Career growth",
    departmentMoves: "Department moves",
    compensationGrowth: "Compensation growth",
    awaitingReview: "Awaiting review",
    thisMonth: "This month",
    selectClient: "Select Client...",
    selectDepartment: "Select Department...",
    selectPerformance: "Select Performance...",
    selectStatus: "Select Status...",
    // Filters & Search
    searchFilterEmployees: "Search & Filter Employees",
    showing: "Showing",
    of: "of",
    employees: "employees",
    forSelectedClient: "for selected client",
    viewHistory: "View History",
    refresh: "Refresh",
    searchEmployee: "Search Employee",
    searchPlaceholder: "Search by name, email, position, or Infinity Code...",
    client: "Client",
    allClients: "All Clients",
    department: "Department",
    allDepartments: "All Departments",
    performance: "Performance",
    allPerformanceLevels: "All Performance Levels",
    status: "Status",
    allStatuses: "All Statuses",
    sortBy: "Sort By",
    name: "Name",
    salary: "Salary",
    joinDate: "Join Date",
    ascendingOrder: "Ascending order",
    descendingOrder: "Descending order",

    // Table
    employee: "Employee",
    actions: "Actions",
    loadingEmployees: "Loading employees...",
    noEmployeesFound: "No Employees Found",
    noEmployeesMatch:
      "No employees match your current search and filter criteria.",
    pleaseWait: "Please wait while we load the employee data...",
    clearFilters: "Clear Filters",

    // Action Types
    selectActionType: "Select Action Type",
    chooseAction: "Choose an action to perform",
    promoteEmployee: "Promote Employee",
    demoteEmployee: "Demote Employee",
    transferEmployee: "Transfer Employee",
    salaryAdjustment: "Salary Adjustment",
    terminateEmployment: "Terminate Employment",
    suspendEmployee: "Suspend Employee",
    rehireEmployee: "Rehire Employee",
    contractUpdate: "Contract Update",
    jobRotation: "Job Rotation",
    performanceReview: "Performance Review",
    trainingAssignment: "Training Assignment",

    // Action Descriptions
    advanceToHigher: "Advance employee to higher position",
    moveToLower: "Move employee to lower position",
    transferDifferent: "Transfer to different department",
    modifyCompensation: "Modify employee compensation",
    endEmployment: "End employment contract",
    temporarySuspension: "Temporary suspension from duties",
    reinstateFormer: "Reinstate former employee",
    modifyContract: "Modify employment contract",
    rotateDifferent: "Rotate to different role/department",
    conductEvaluation: "Conduct performance evaluation",
    assignTraining: "Assign to training program",

    // Approval Levels
    requiresHRManager: "Requires HR Manager approval",
    requiresDeptHead: "Requires Department Head approval",
    requiresBothManagers: "Requires Both Managers approval",
    requiresFinanceManager: "Requires Finance Manager approval",
    requiresHRDirector: "Requires HR Director approval",
    requiresLegal: "Requires Legal Department approval",
    requiresDirectManager: "Requires Direct Manager approval",
    requiresTrainingManager: "Requires Training Manager approval",

    // Form Fields - Common
    effectiveDate: "Effective Date",
    reasonForAction: "Reason for Action",
    provideDetailedReason: "Provide detailed reason for this action...",
    approverNotes: "Approver Notes",
    addAdditionalNotes: "Add any additional notes or comments...",
    approvalRequired: "Approval Required",
    thisActionRequires: "This action requires approval from:",

    // Promote Fields
    newPosition: "New Position",
    enterNewPosition: "Enter new position title...",
    newSalary: "New Salary",
    enterNewSalary: "Enter new salary amount...",
    newDepartment: "New Department",
    keepCurrentDept: "Keep Current Department",
    currentSalary: "Current",
    newSalaryAmount: "New",

    // Demote Fields
    demotionReasonCategory: "Demotion Reason Category",
    selectReason: "Select Reason",
    performanceIssues: "Performance Issues",
    companyRestructuring: "Company Restructuring",
    misconduct: "Misconduct",
    businessNeeds: "Business Needs",
    other: "Other",

    // Transfer Fields
    selectDepartment: "Select Department",
    keepCurrentPosition: "Keep current position or enter new one...",
    newManager: "New Manager",
    transferType: "Transfer Type",
    selectType: "Select Type",
    permanentTransfer: "Permanent Transfer",
    temporaryTransfer: "Temporary Transfer",
    projectBased: "Project-Based",

    // Salary Change Fields
    adjustmentType: "Adjustment Type",
    annualIncrease: "Annual Increase",
    promotion: "Promotion",
    marketAdjustment: "Market Adjustment",
    performanceBased: "Performance-Based",
    costOfLiving: "Cost of Living",
    salaryCorrection: "Salary Correction",

    // Terminate Fields
    terminationType: "Termination Type",
    voluntaryResignation: "Voluntary Resignation",
    involuntaryTermination: "Involuntary Termination",
    retirement: "Retirement",
    contractEnd: "Contract End",
    mutualAgreement: "Mutual Agreement",
    layoff: "Layoff/Redundancy",
    lastWorkingDay: "Last Working Day",
    terminationReason: "Termination Reason",
    provideDetailedTermination: "Provide detailed termination reason...",
    severancePackage: "Severance Package",
    enterSeveranceAmount: "Enter severance amount if applicable...",
    eligibleForRehire: "Eligible for Rehire?",
    yes: "Yes",
    no: "No",
    conditional: "Conditional",

    suspensionStartDate: "Suspension Start Date",
    suspensionEndDate: "Suspension End Date",
    suspensionType: "Suspension Type",
    withPay: "With Pay",
    withoutPay: "Without Pay",
    partialPay: "Partial Pay",
    suspensionReason: "Suspension Reason",
    provideDetailedSuspension: "Provide detailed suspension reason...",

    // Rehire Fields
    position: "Position",
    enterPositionTitle: "Enter position title...",
    startingSalary: "Starting Salary",
    enterStartingSalary: "Enter starting salary...",
    rehireDate: "Rehire Date",

    // Contract Fields
    contractType: "Contract Type",
    selectContractType: "Select Contract Type",
    permanent: "Permanent",
    fixedTerm: "Fixed Term",
    temporary: "Temporary",
    partTime: "Part Time",
    consultant: "Consultant",
    contractor: "Contractor",
    contractStartDate: "Contract Start Date",
    contractEndDate: "Contract End Date",
    contractTerms: "Contract Terms",
    enterContractTerms: "Enter any special contract terms or conditions...",
    selectDepartment: "Select Department",

    // Rotation Fields
    rotationDepartment: "Rotation Department",
    rotationRole: "Rotation Role",
    enterRotationRole: "Enter rotation role...",
    durationMonths: "Duration (months)",
    enterDuration: "Enter duration in months...",
    rotationObjectives: "Rotation Objectives",
    enterObjectives: "Enter learning objectives and goals...",

    // Review Fields
    performanceRating: "Performance Rating",
    selectRating: "Select Rating",
    excellent: "Excellent",
    veryGood: "Very Good",
    good: "Good",
    average: "Average",
    belowAverage: "Below Average",
    poor: "Poor",
    reviewScore: "Review Score (1-10)",
    enterScore: "Enter score...",
    strengths: "Strengths",
    listStrengths: "List employee strengths and achievements...",
    areasForImprovement: "Areas for Improvement",
    listImprovements: "List areas that need improvement...",
    nextReviewDate: "Next Review Date",
    fromYourTeam: "from your team",
    myTeam: "My Team",

    // Training Fields
    trainingProgram: "Training Program",
    enterTrainingProgram: "Enter training program name...",
    trainingStartDate: "Training Start Date",
    trainingEndDate: "Training End Date",
    trainingCost: "Training Cost",
    enterTrainingCost: "Enter training cost...",
    trainingObjectives: "Training Objectives",
    enterTrainingObjectives:
      "Enter training objectives and expected outcomes...",

    // Modal Actions
    selectAction: "Select an Action",
    selectActionToProceed:
      "Choose an action type from the dropdown above to proceed with employee management",
    employeeAction: "Employee Action",
    employeeName: "Employee Name",
    infinityCode: "Infinity Code",
    currentPosition: "Current Position",
    currentDepartment: "Current Department",
    reportsTo: "Reports To",
    cancel: "Cancel",
    submitAction: "Submit Action",
    showMore: "Show More",
    remaining: "remaining",

    // Confirmation Modal
    confirmAction: "Confirm Action",
    reviewBeforeConfirm:
      "Please review the details before confirming this action",
    actionDetails: "Action Details",
    actionType: "Action Type",
    reason: "Reason",
    confirmSubmit: "Confirm & Submit",
    processing: "Processing...",

    // History Modal
    actionHistory: "Action History",
    date: "Date",
    details: "Details",
    noActionHistory: "No Action History",
    noActionsRecorded:
      "No employee actions have been recorded yet. Start by taking an action on an employee.",
    noDetailsProvided: "No details provided",

    // Pending Approvals Modal
    pendingApprovalsTitle: "Pending Approvals",
    actionsAwaiting: "actions awaiting approval",
    requestedDate: "Requested Date",
    approvalLevel: "Approval Level",
    approve: "Approve",
    reject: "Reject",
    noPendingApprovals: "No Pending Approvals",
    allActionsReviewed:
      "All actions have been reviewed. There are no pending approvals at this time.",

    // Employee History Modal
    employeeHistoryTitle: "Employee History",
    currentInfo: "Current Information",
    actionTimeline: "Action Timeline",
    records: "records",
    noHistoryFound: "No History Found",
    noActionsForEmployee:
      "No actions have been recorded for this employee yet.",

    // Statuses
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    completed: "Completed",
    unknown: "Unknown",

    // Messages
    successActionApproved: "Action approved successfully",
    successActionRejected: "Action rejected successfully",
    errorApprovingAction: "Failed to approve action. Please try again.",
    errorRejectingAction: "Failed to reject action. Please try again.",
    errorLoadingEmployees: "Failed to load employees. Please try again.",
    pleaseSelectAction: "Please select an action type",
    effectiveDateRequired: "Effective date is required",
    reasonRequired: "Reason for action is required",
    approverNotesRequired: "Approver notes are required",
    actionSubmittedSuccess: "successfully submitted for",
    awaitingApproval: "Awaiting approval",
    salaryWillIncrease: "Salary will increase by EGP",
    salaryWillDecrease: "Salary will decrease by EGP",
    changesAppliedOnApproval:
      "Changes will be applied to employee record and payroll upon approval.",
    changesAppliedImmediately:
      "Changes will be applied to employee record and payroll immediately.",
    errorProcessingAction: "Error processing action.",
    pleaseTryAgain: "Please try again or contact support.",

    // Misc
    notAvailable: "N/A",
    egp: "EGP",
    searchForManagers: "Search for managers...",
    noManagersFound: "No managers found",
    tryDifferentSearch: "Try a different search term",
    noManagersAvailable: "No managers available",
  },
};
// Helper function to get authorization headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };
};

// ===================================
// MANAGER AUTOCOMPLETE COMPONENT
// ===================================
const ManagerAutocomplete = ({
  value,
  onChange,
  theme,
  styles,
  language,
  t,
}) => {
  const { profile } = useProfile();
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [availableManagers, setAvailableManagers] = useState([]);
  const dropdownRef = useRef(null);
  const isRTL = language === "ar";

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const clientName = profile?.clientName || localStorage.getItem("clientName");
        const viewType = (localStorage.getItem("managerViewType") || "manager").toLowerCase();

        if (!clientName) {
          console.error("No clientName found in localStorage");
          return;
        }

        console.log(`[ManagerAutocomplete] Fetching managers - viewType: ${viewType}, clientName: ${clientName}`);

        let data;
        if (viewType === "admin") {
          // Admin view: use visibility rules
          console.log("[ManagerAutocomplete] Using ADMIN view");
          data = await employeeActionsAPI.getEmployeesByVisibility(clientName, "admin");
        } else {
          // Manager view: use hierarchy
          console.log("[ManagerAutocomplete] Using MANAGER view");
          data = await employeeActionsAPI.getEmployeesByClientbymanager(
            clientName,
            profile?.infinityCode || localStorage.getItem("infinityCode")
          );
        }

        if (!Array.isArray(data)) {
          console.error("Invalid data format received");
          return;
        }

        const managerInfinityCodes = [
          ...new Set(
            data
              .map((emp) => emp.firstLineManagerInfinityCode)
              .filter((code) => code != null)
          ),
        ];

        const managers = data.filter((emp) =>
          managerInfinityCodes.includes(emp.infinityCode)
        );

        setAvailableManagers(managers);
      } catch (error) {
        console.error("Error fetching managers:", error);
        setAvailableManagers([]);
      }
    };

    if (showDropdown) {
      fetchManagers();
    }
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getEmployeeName = (manager) => {
    return language === "ar"
      ? manager.arabicName || manager.sr || t.notAvailable
      : manager.sr || manager.arabicName || t.notAvailable;
  };

  const filteredManagers = availableManagers.filter(
    (manager) =>
      searchTerm === "" ||
      (manager.sr || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (manager.arabicName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const handleSelect = (manager) => {
    onChange({
      target: {
        name: "reportsTo",
        value: getEmployeeName(manager),
      },
    });
    setSearchTerm("");
    setShowDropdown(false);
  };

  const handleRemove = () => {
    onChange({
      target: {
        name: "reportsTo",
        value: "",
      },
    });
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        direction: isRTL ? "rtl" : "ltr",
      }}
      ref={dropdownRef}
    >
      {value && (
        <div
          style={{
            background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
            padding: "clamp(10px, 1.5vw, 12px) clamp(14px, 2.2vw, 16px)",
            borderRadius: "clamp(10px, 1.5vw, 12px)",
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "10px",
            color: "white",
            fontSize: "clamp(13px, 1.9vw, 15px)",
            fontWeight: "700",
            animation: "fadeIn 0.3s ease",
          }}
        >
          <Users size={16} />
          <span>{value}</span>
          <button
            type="button"
            onClick={handleRemove}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              borderRadius: "50%",
              width: "20px",
              height: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              padding: 0,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.3)";
              e.target.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.2)";
              e.target.style.transform = "scale(1)";
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}





      {!value && (
        <div style={{ position: "relative" }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            style={{
              ...styles.input,
              paddingRight: isRTL
                ? "clamp(14px, 2.2vw, 18px)"
                : "clamp(48px, 7vw, 54px)",
              paddingLeft: isRTL
                ? "clamp(48px, 7vw, 54px)"
                : "clamp(14px, 2.2vw, 18px)",
              textAlign: isRTL ? "right" : "left",
            }}
            className="enhanced-input-focus"
            placeholder={t.searchForManagers}
          />
          <Search
            size={18}
            style={{
              position: "absolute",
              [isRTL ? "left" : "right"]: "clamp(14px, 2.2vw, 18px)",
              top: "50%",
              transform: "translateY(-50%)",
              color: theme === "dark" ? "rgba(255,255,255,0.6)" : "#94a3b8",
              pointerEvents: "none",
            }}
          />
        </div>
      )}

      {showDropdown && !value && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            [isRTL ? "right" : "left"]: 0,
            [isRTL ? "left" : "right"]: 0,
            background:
              theme === "dark"
                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.98))"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98))",
            borderRadius: "clamp(12px, 1.8vw, 14px)",
            backdropFilter: "blur(25px)",
            border:
              theme === "dark"
                ? "2px solid rgba(255,255,255,0.15)"
                : "2px solid rgba(0,0,0,0.08)",
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
            scrollbarWidth: "none", // للـ Firefox
            msOverflowStyle: "none", // للـ IE و Edge
            WebkitOverflowScrolling: "touch", // للـ iOS
            scrollbarWidth: "none", // للـ Firefox
            msOverflowStyle: "none", // للـ IE و Edge
            animation: "dropdownSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        >
          {filteredManagers.length > 0 ? (
            filteredManagers.map((manager) => (
              <div
                key={manager.id}
                onClick={() => handleSelect(manager)}
                style={{
                  padding: "clamp(12px, 1.8vw, 14px) clamp(14px, 2.2vw, 16px)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                  gap: "clamp(10px, 1.5vw, 12px)",
                  borderBottom:
                    theme === "dark"
                      ? "1px solid rgba(255,255,255,0.08)"
                      : "1px solid rgba(0,0,0,0.06)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    theme === "dark"
                      ? "rgba(59, 130, 246, 0.15)"
                      : "rgba(59, 130, 246, 0.1)";
                  e.currentTarget.style.transform = isRTL
                    ? "translateX(-4px)"
                    : "translateX(4px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              >
                <div
                  style={{
                    width: "clamp(36px, 5.5vw, 42px)",
                    height: "clamp(36px, 5.5vw, 42px)",
                    borderRadius: "clamp(10px, 1.5vw, 12px)",
                    background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "800",
                    fontSize: "clamp(14px, 2vw, 16px)",
                    color: "white",
                    flexShrink: 0,
                  }}
                >
                  {getEmployeeName(manager)
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .substring(0, 2)
                    .toUpperCase()}
                </div>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "800",
                      fontSize: "clamp(13px, 1.9vw, 15px)",
                      color: theme === "dark" ? "#ffffff" : "#0f172a",
                      marginBottom: "2px",
                    }}
                  >
                    {getEmployeeName(manager)}
                  </div>
                  <div
                    style={{
                      fontSize: "clamp(11px, 1.6vw, 12px)",
                      color:
                        theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                      display: "flex",
                      flexDirection: isRTL ? "row" : "row",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <Briefcase size={12} />
                    {manager.employeeType || t.notAvailable}
                  </div>
                  <div
                    style={{
                      fontSize: "clamp(10px, 1.5vw, 11px)",
                      color:
                        theme === "dark" ? "rgba(255,255,255,0.5)" : "#94a3b8",
                      marginTop: "2px",
                    }}
                  >
                    Code: {manager.infinityCode}
                  </div>
                </div>
                <ArrowRight
                  size={18}
                  style={{
                    color:
                      theme === "dark" ? "rgba(255,255,255,0.4)" : "#94a3b8",
                    flexShrink: 0,
                    transform: isRTL ? "rotate(180deg)" : "none",
                  }}
                />
              </div>
            ))
          ) : (
            <div
              style={{
                padding: "clamp(20px, 3vw, 24px)",
                textAlign: "center",
                color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
              }}
            >
              <AlertCircle
                size={32}
                style={{ marginBottom: "10px", opacity: 0.5 }}
              />
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "clamp(12px, 1.7vw, 14px)",
                }}
              >
                {t.noManagersFound}
              </div>
              <div
                style={{
                  fontSize: "clamp(11px, 1.6vw, 12px)",
                  marginTop: "4px",
                }}
              >
                {searchTerm ? t.tryDifferentSearch : t.noManagersAvailable}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ===================================
// STYLES FUNCTION
// ===================================
const createStyles = (theme, isRTL) => ({
  container: {
    padding: "clamp(16px, 3vw, 24px)",
    fontFamily: isRTL
      ? "'Cairo', 'Tajawal', -apple-system, BlinkMacSystemFont, sans-serif"
      : "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100vw",
    overflowX: "hidden",
    boxSizing: "border-box",
    background:
      theme === "dark"
        ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
        : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
    position: "relative",
    direction: isRTL ? "rtl" : "ltr",
  },
  headerCard: {
    background:
      theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.98)",
    padding: "clamp(20px, 3vw, 28px)",
    marginTop: 0,
    marginLeft: isRTL ? 0 : 0,
    marginRight: isRTL ? 0 : 0,
    borderRadius: "clamp(16px, 2.5vw, 22px)",
    transition: "all 0.3s ease",
    backdropFilter: "blur(20px)",
    border:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.06)",
    marginBottom: "clamp(24px, 4vw, 32px)",
    cursor: "pointer",
  },
  headerGradientOverlay: {
    position: "absolute",
    top: 0,
    [isRTL ? "left" : "right"]: 0,
    width: "50%",
    height: "100%",
    // background:
    //   theme === "dark"
    //     ? `radial-gradient(circle at top ${isRTL ? 'left' : 'right'}, rgba(59, 130, 246, 0.2), transparent 70%)`
    //     : `radial-gradient(circle at top ${isRTL ? 'left' : 'right'}, rgba(59, 130, 246, 0.1), transparent 70%)`,
    // pointerEvents: "none",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))",
    gap: "clamp(18px, 3vw, 24px)",
    marginBottom: -1,
  },
  statCard: {
    background:
      theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.98)",
    padding: "clamp(20px, 3vw, 28px)",
    marginTop: 0,
    marginLeft: isRTL ? 0 : 0,
    marginRight: isRTL ? 0 : 0,
    borderRadius: "clamp(16px, 2.5vw, 22px)",
    transition: "all 0.3s ease",
    backdropFilter: "blur(20px)",
    border:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.06)",
    marginBottom: "clamp(24px, 4vw, 32px)",
    cursor: "pointer",
  },
  statIconWrapper: {
    width: "clamp(52px, 9vw, 64px)",
    height: "clamp(52px, 9vw, 64px)",
    borderRadius: "clamp(14px, 2.2vw, 18px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "clamp(14px, 2.2vw, 18px)",
    position: "relative",
  },
  statValue: {
    fontSize: "clamp(28px, 5.5vw, 36px)",
    fontWeight: "900",
    color: theme === "dark" ? "#fff" : "#0f172a",
    marginBottom: "6px",
    letterSpacing: "-0.03em",
    textAlign: isRTL ? "right" : "left",
  },
  statLabel: {
    fontSize: "clamp(12px, 1.8vw, 14px)",
    color: theme === "dark" ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.65)",
    fontWeight: "700",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    textAlign: isRTL ? "right" : "left",
  },
  statTrend: {
    fontSize: "clamp(11px, 1.6vw, 13px)",
    fontWeight: "700",
    display: "flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    gap: "6px",
    marginTop: "10px",
  },
  welcomeTitle: {
    fontSize: "25px",
    fontWeight: "900",
    color: theme === "dark" ? "#fff" : "#0f172a",
    margin: "0 0 14px 0",
    letterSpacing: "-0.03em",
    lineHeight: "1.1",
    position: "relative",
    zIndex: 1,
    textAlign: isRTL ? "right" : "left",
    display: "flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    gap: "14px",
  },
  welcomeSubtitle: {
    fontSize: "clamp(14px, 2.2vw, 17px)",
    color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(15,23,42,0.75)",
    lineHeight: "1.6",
    fontWeight: "500",
    position: "relative",
    zIndex: 1,
    textAlign: isRTL ? "right" : "left",
  },
  filterCard: {
    background:
      theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.98)",
    padding: "clamp(26px, 4.5vw, 36px)",
    borderRadius: "clamp(16px, 2.5vw, 22px)",
    marginBottom: "clamp(24px, 4vw, 32px)",
    backdropFilter: "blur(20px)",
    border:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.06)",
    position: "relative",
    zIndex: 10,
  },
  controlsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))",
    gap: "clamp(18px, 3vw, 22px)",
    marginTop: "clamp(22px, 3.5vw, 28px)",
  },
  controlGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "clamp(10px, 1.6vw, 12px)",
  },
  label: {
    color: theme === "dark" ? "rgba(255,255,255,0.95)" : "#1e293b",
    fontSize: "clamp(12px, 1.7vw, 14px)",
    fontWeight: "800",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    display: "flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    gap: "clamp(8px, 1.2vw, 10px)",
  },
  input: {
    width: "100%",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "16px",
    paddingRight: "16px",
    borderRadius: "12px",
    border: "1px solid rgba(148, 163, 184, 0.3)",
    background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
    color: theme === "dark" ? "#f1f5f9" : "#334155",
    fontSize: "14px",
    fontWeight: "500",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    textAlign: isRTL ? "right" : "left",
    direction: isRTL ? "rtl" : "ltr",
  },
  textarea: {
    width: "100%",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "16px",
    paddingRight: "16px",
    borderRadius: "12px",
    border: "1px solid rgba(148, 163, 184, 0.3)",
    background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
    color: theme === "dark" ? "#f1f5f9" : "#334155",
    fontSize: "14px",
    fontWeight: "500",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    minHeight: "120px",
    resize: "vertical",
    fontFamily: "inherit",
    lineHeight: "1.6",
    textAlign: isRTL ? "right" : "left",
    direction: isRTL ? "rtl" : "ltr",
  },
  select: {
    width: "10%",
    height: "100%",
    //paddingTop: "12px",
    //paddingBottom: "12px",
    //paddingLeft: isRTL ? "36px" : "16px",
    //paddingRight: isRTL ? "16px" : "36px",
    borderRadius: "12px",
    border: "1px solid rgba(148, 163, 184, 0.3)",
    background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
    color: theme === "dark" ? "#f1f5f9" : "#334155",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    outline: "none",
    transition: "all 0.3s ease",
    textAlign: isRTL ? "right" : "left",
    direction: isRTL ? "rtl" : "ltr",
    boxSizing: "border-box",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    ":-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px rgba(226, 232, 240, 0.6) inset !important",
      WebkitTextFillColor: "#334155 !important",
    },
  },
  selectOption: {
    //background: theme === "dark" ? "#1e293b" : "#ffffff",
    //color: theme === "dark" ? "#ffffff" : "#0f172a",
    padding: "12px",
    fontWeight: "600",
    borderRadius: "clamp(12px, 1.8vw, 14px)",
    marginBottom: 30,
  },

  selectWrapper: {
    position: "relative",
    width: "100%",
  },
  selectChevron: {
    position: "absolute",
    [isRTL ? "left" : "right"]: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    color: theme === "dark" ? "rgba(255,255,255,0.6)" : "#334155",
  },

  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    [isRTL ? "right" : "left"]: "clamp(14px, 2.2vw, 18px)",
    color: theme === "dark" ? "rgba(255,255,255,0.6)" : "#94a3b8",
    pointerEvents: "none",
  },
  customDropdown: {
    position: "relative",
    width: "100%",
  },
  dropdownTrigger: {
    background: theme === "dark" ? "rgba(255, 255, 255, 0.08)" : "#ffffff",
    border:
      theme === "dark"
        ? "2px solid rgba(255, 255, 255, 0.18)"
        : "2px solid rgba(0, 0, 0, 0.1)",
    borderRadius: "clamp(14px, 2vw, 16px)",
    padding: "clamp(16px, 2.5vw, 18px) clamp(18px, 2.8vw, 20px)",
    color: theme === "dark" ? "#ffffff" : "#0f172a",
    fontSize: "clamp(14px, 2vw, 16px)",
    fontWeight: "700",
    outline: "none",
    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
    width: "100%",
    cursor: "pointer",
    backdropFilter: "blur(15px)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "14px",
  },
  dropdownTriggerContent: {
    display: "flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    gap: "clamp(12px, 1.8vw, 14px)",
    flex: 1,
    minWidth: 0,
  },
  dropdownIconWrapper: {
    width: "clamp(42px, 6.5vw, 48px)",
    height: "clamp(42px, 6.5vw, 48px)",
    borderRadius: "clamp(10px, 1.5vw, 12px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  dropdownTextWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
    flex: 1,
    textAlign: isRTL ? "right" : "left",
  },
  dropdownLabel: {
    fontSize: "clamp(14px, 2vw, 16px)",
    fontWeight: "800",
    lineHeight: "1.2",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dropdownDescription: {
    fontSize: "clamp(11px, 1.6vw, 12px)",
    color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
    fontWeight: "600",
    lineHeight: "1.3",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dropdownChevron: {
    flexShrink: 0,
  },
  dropdownChevronOpen: {
    transform: "rotate(180deg)",
  },
  dropdownMenu: {
    position: "absolute",
    top: "calc(100% + 10px)",
    [isRTL ? "right" : "left"]: 0,
    [isRTL ? "left" : "right"]: 0,
    background:
      theme === "dark"
        ? "linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.98))"
        : "linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98))",
    borderRadius: "clamp(14px, 2vw, 16px)",
    backdropFilter: "blur(25px)",
    border:
      theme === "dark"
        ? "2px solid rgba(255,255,255,0.15)"
        : "2px solid rgba(0,0,0,0.08)",
    zIndex: 1000,
    maxHeight: "clamp(320px, 45vh, 450px)",
    overflowY: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    WebkitOverflowScrolling: "touch",
    animation: "dropdownSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
  },
  dropdownMenuItem: {
    padding: "clamp(14px, 2.2vw, 16px) clamp(16px, 2.5vw, 18px)",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
    display: "flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    gap: "clamp(12px, 1.8vw, 14px)",
    borderBottom:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.06)",
  },
  dropdownMenuItemSelected: {
    background:
      theme === "dark" ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.12)",
  },
  tableCard: {
    background:
      theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.98)",
    borderRadius: "clamp(16px, 2.5vw, 22px)",
    backdropFilter: "blur(20px)",
    border:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.06)",
    overflow: "hidden",
    position: "relative",
    marginBottom: "40px",
  },
  tableWrapper: {
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "thin",
    scrollbarColor:
      theme === "dark"
        ? "rgba(255,255,255,0.3) rgba(255,255,255,0.1)"
        : "rgba(0,0,0,0.3) rgba(0,0,0,0.1)",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: "900px",
    direction: isRTL ? "rtl" : "ltr",
    borderRadius: "16px",
    overflow: "hidden",
  },
  th: {
    background:
      theme === "dark"
        ? "linear-gradient(135deg, rgba(51, 65, 85, 0.95), rgba(71, 85, 105, 0.95))"
        : "linear-gradient(135deg, rgba(248, 250, 252, 1), rgba(241, 245, 249, 1))",
    color: theme === "dark" ? "#f1f5f9" : "#1e293b",
    padding: "clamp(18px, 2.8vw, 22px) clamp(18px, 2.8vw, 22px)",
    textAlign: isRTL ? "right" : "left",
    fontSize: "clamp(11px, 1.7vw, 13px)",
    fontWeight: "900",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    borderBottom:
      theme === "dark"
        ? "2px solid rgba(59, 130, 246, 0.5)"
        : "2px solid rgba(59, 130, 246, 0.2)",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 10,
    backdropFilter: "blur(25px)",
  },
  td: {
    padding: "clamp(18px, 2.8vw, 22px) clamp(18px, 2.8vw, 22px)",
    fontSize: "clamp(13px, 1.9vw, 15px)",
    fontWeight: "600",
    color: theme === "dark" ? "#f8fafc" : "#0f172a",
    borderBottom:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    whiteSpace: "nowrap",
    textAlign: isRTL ? "right" : "left",
  },
  employeeCell: {
    display: "flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    gap: "clamp(14px, 2.2vw, 18px)",
  },
  employeeAvatar: {
    width: "clamp(44px, 7vw, 52px)",
    height: "clamp(44px, 7vw, 52px)",
    borderRadius: "150%",
    background: "linear-gradient(135deg, #2e7cba 0%, #22354d 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "800",
    fontSize: "clamp(16px, 2.8vw, 20px)",
    color: "white",
    border: "0.5px solid #22354d",
    flexShrink: 0,
    direction: "ltr",
  },
  employeeInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    minWidth: 0,
    textAlign: isRTL ? "right" : "left",
  },
  employeeName: {
    fontWeight: "800",
    fontSize: "clamp(14px, 2vw, 16px)",
    color: theme === "dark" ? "#ffffff" : "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  employeeDetail: {
    fontSize: "clamp(11px, 1.6vw, 13px)",
    color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
    display: "flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    gap: "6px",
    fontWeight: "600",
  },
  badge: {
    display: "inline-flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    gap: "5px",
    padding: "clamp(5px, 1vw, 7px) clamp(12px, 1.8vw, 14px)",
    borderRadius: "clamp(7px, 1.1vw, 9px)",
    fontSize: "clamp(10px, 1.5vw, 12px)",
    fontWeight: "800",
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  actionButton: {
    padding: "clamp(10px, 1.5vw, 12px) clamp(18px, 2.5vw, 20px)",
    borderRadius: "clamp(10px, 1.5vw, 12px)",
    border: "none",
    color: "white",
    cursor: "pointer",
    fontSize: "clamp(12px, 1.7vw, 14px)",
    fontWeight: "800",
    display: "inline-flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    width: "100%",
  },
  primaryButton: {
    background:
      theme === "dark" ? "rgba(59, 130, 246, 0.15)" : "rgba(59, 130, 246, 0.1)",
    border:
      theme === "dark"
        ? "1px solid rgba(59, 130, 246, 0.4)"
        : "1px solid rgba(59, 130, 246, 0.3)",
    borderRadius: "clamp(12px, 1.8vw, 14px)",
    padding: "clamp(14px, 2.2vw, 16px) clamp(22px, 3.2vw, 26px)",
    cursor: "pointer",
    color: theme === "dark" ? "#ffffff" : "#ffffff",
    fontWeight: "800",
    fontSize: "clamp(13px, 1.9vw, 15px)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "clamp(10px, 1.5vw, 12px)",
    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  secondaryButton: {
    background: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
    color: theme === "dark" ? "#ffffff" : "#1e293b",
    border:
      theme === "dark"
        ? "2px solid rgba(255,255,255,0.18)"
        : "2px solid rgba(0,0,0,0.1)",
    borderRadius: "clamp(12px, 1.8vw, 14px)",
    padding: "clamp(14px, 2.2vw, 16px) clamp(22px, 3.2vw, 26px)",
    fontSize: "clamp(13px, 1.9vw, 15px)",
    fontWeight: "800",
    cursor: "pointer",
    display: "inline-flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "clamp(10px, 1.5vw, 12px)",
    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
    backdropFilter: "blur(15px)",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    backdropFilter: "blur(16px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    animation: "fadeIn 0.3s ease",
    padding: "clamp(18px, 3vw, 24px)",
  },
  modalContent: {
    background:
      theme === "dark"
        ? "linear-gradient(135deg, rgba(30, 41, 59, 0.99), rgba(51, 65, 85, 0.99))"
        : "linear-gradient(135deg, rgba(255, 255, 255, 0.99), rgba(248, 250, 252, 0.99))",
    padding: "clamp(28px, 4.5vw, 36px)",
    borderRadius: "clamp(18px, 2.8vw, 26px)",
    width: "1100px",
    maxHeight: "90vh",
    overflowY: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    WebkitOverflowScrolling: "touch",
    color: theme === "dark" ? "#ffffff" : "#0f172a",
    border:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.18)"
        : "1px solid rgba(0,0,0,0.08)",
    boxSizing: "border-box",
    backdropFilter: "blur(30px)",
    direction: isRTL ? "rtl" : "ltr",
  },
  modalHeader: {
    display: "flex",
    flexDirection: isRTL ? "row" : "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "clamp(22px, 3.5vw, 28px)",
    paddingBottom: "clamp(18px, 2.8vw, 22px)",
    borderBottom:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.1)",
  },
  modalTitle: {
    fontSize: "clamp(20px, 3.8vw, 26px)",
    fontWeight: "900",
    margin: 0,
    letterSpacing: "-0.02em",
    textAlign: isRTL ? "right" : "left",
  },
  modalIconWrapper: {
    width: "clamp(48px, 7.5vw, 56px)",
    height: "clamp(48px, 7.5vw, 56px)",
    borderRadius: "clamp(13px, 2vw, 16px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [isRTL ? "marginLeft" : "marginRight"]: "clamp(14px, 2.2vw, 18px)",
  },
  closeButton: {
    background: theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
    border: "none",
    cursor: "pointer",
    color: theme === "dark" ? "white" : "#0f172a",
    padding: "clamp(10px, 1.5vw, 12px)",
    borderRadius: "clamp(10px, 1.5vw, 12px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
  },
  infoBox: {
    background: theme === "dark" ? "rgba(255,255,255,0.06)" : "#f8fafc",
    padding: "clamp(18px, 2.8vw, 22px)",
    borderRadius: "clamp(14px, 2.2vw, 18px)",
    border:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.12)"
        : "1px solid rgba(0,0,0,0.08)",
    marginBottom: "clamp(18px, 2.8vw, 22px)",
  },
  infoRow: {
    display: "flex",
    flexDirection: isRTL ? "row" : "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "clamp(12px, 1.8vw, 14px)",
    paddingBottom: "clamp(12px, 1.8vw, 14px)",
    borderBottom:
      theme === "dark"
        ? "1px solid rgba(255,255,255,0.1)"
        : "1px solid rgba(0,0,0,0.06)",
  },
  infoLabel: {
    fontSize: "clamp(11px, 1.6vw, 13px)",
    color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    textAlign: isRTL ? "right" : "left",
  },
  infoValue: {
    fontSize: "clamp(14px, 2vw, 16px)",
    fontWeight: "800",
    color: theme === "dark" ? "#ffffff" : "#0f172a",
    textAlign: isRTL ? "left" : "right",
  },
  alertBox: {
    padding: "clamp(16px, 2.4vw, 18px)",
    borderRadius: "clamp(12px, 1.8vw, 14px)",
    fontSize: "clamp(12px, 1.7vw, 14px)",
    fontWeight: "600",
    display: "flex",
    flexDirection: isRTL ? "row" : "row",
    alignItems: "center",
    gap: "clamp(12px, 1.8vw, 14px)",
    marginBottom: "clamp(18px, 2.8vw, 22px)",
  },
  successAlert: {
    background: "#dcfce7",
    border: "1px solid #86efac",
    color: "#166534",
  },
  errorAlert: {
    background: "#fee2e2",
    border: "1px solid #fca5a5",
    color: "#991b1b",
  },
  warningAlert: {
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    color: "#92400e",
  },
  emptyState: {
    padding: "clamp(70px, 10vw, 100px) clamp(22px, 3.5vw, 44px)",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyStateIcon: {
    marginBottom: "clamp(22px, 3.5vw, 28px)",
    opacity: 0.3,
    color: theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
  },
  emptyStateTitle: {
    color: theme === "dark" ? "rgba(255,255,255,0.95)" : "#1e293b",
    fontSize: "clamp(20px, 3.5vw, 26px)",
    fontWeight: "800",
    marginBottom: "clamp(12px, 1.8vw, 14px)",
    letterSpacing: "-0.02em",
  },
  emptyStateText: {
    color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
    fontSize: "clamp(14px, 2vw, 16px)",
    lineHeight: "1.6",
    maxWidth: "450px",
    fontWeight: "500",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))",
    gap: "clamp(18px, 2.8vw, 22px)",
    marginTop: "clamp(20px, 3vw, 24px)",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      theme === "dark" ? "rgba(15, 23, 42, 0.8)" : "rgba(255, 255, 255, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    borderRadius: "clamp(16px, 2.5vw, 22px)",
    backdropFilter: "blur(10px)",
  },
  loadingSpinner: {
    animation: "spin 1s linear infinite",
  },
});

// ===================================
// ACTION TYPES CONFIGURATION
// ===================================
const ACTION_TYPES = [
  {
    id: "promote",
    labelKey: "promoteEmployee",
    icon: TrendingUp,
    color: "linear-gradient(135deg, #10b981, #059669)",
    bgColor: "rgba(16, 185, 129, 0.15)",
    borderColor: "rgba(16, 185, 129, 0.4)",
    descriptionKey: "advanceToHigher",
    requiresApproval: true,
    approvalLevelKey: "requiresHRManager",
  },
  {
    id: "demote",
    labelKey: "demoteEmployee",
    icon: TrendingDown,
    color: "linear-gradient(135deg, #ef4444, #dc2626)",
    bgColor: "rgba(239, 68, 68, 0.15)",
    borderColor: "rgba(239, 68, 68, 0.4)",
    descriptionKey: "moveToLower",
    requiresApproval: true,
    approvalLevelKey: "requiresDeptHead",
  },
  {
    id: "transfer",
    labelKey: "transferEmployee",
    icon: MapPin,
    color: "linear-gradient(135deg, #2e7cba, #2e7cba)",
    bgColor: "rgba(59, 130, 246, 0.15)",
    borderColor: "rgba(59, 130, 246, 0.4)",
    descriptionKey: "transferDifferent",
    requiresApproval: true,
    approvalLevelKey: "requiresBothManagers",
  },
  {
    id: "salary_change",
    labelKey: "salaryAdjustment",
    icon: DollarSign,
    color: "linear-gradient(135deg, #f59e0b, #d97706)",
    bgColor: "rgba(245, 158, 11, 0.15)",
    borderColor: "rgba(245, 158, 11, 0.4)",
    descriptionKey: "modifyCompensation",
    requiresApproval: true,
    approvalLevelKey: "requiresFinanceManager",
  },
  {
    id: "terminate",
    labelKey: "terminateEmployment",
    icon: UserX,
    color: "linear-gradient(135deg, #dc2626, #991b1b)",
    bgColor: "rgba(220, 38, 38, 0.15)",
    borderColor: "rgba(220, 38, 38, 0.4)",
    descriptionKey: "endEmployment",
    requiresApproval: true,
    approvalLevelKey: "requiresHRDirector",
  },
  {
    id: "suspend",
    labelKey: "suspendEmployee",
    icon: UserMinus,
    color: "linear-gradient(135deg, #f97316, #ea580c)",
    bgColor: "rgba(249, 115, 22, 0.15)",
    borderColor: "rgba(249, 115, 22, 0.4)",
    descriptionKey: "temporarySuspension",
    requiresApproval: true,
    approvalLevelKey: "requiresHRManager",
  },
  {
    id: "rehire",
    labelKey: "rehireEmployee",
    icon: RefreshCw,
    color: "linear-gradient(135deg, #2e7cba, #7c3aed)",
    bgColor: "rgba(139, 92, 246, 0.15)",
    borderColor: "rgba(139, 92, 246, 0.4)",
    descriptionKey: "reinstateFormer",
    requiresApproval: true,
    approvalLevelKey: "requiresHRDirector",
  },
  {
    id: "contract",
    labelKey: "contractUpdate",
    icon: FileCheck,
    color: "linear-gradient(135deg, #06b6d4, #0891b2)",
    bgColor: "rgba(6, 182, 212, 0.15)",
    borderColor: "rgba(6, 182, 212, 0.4)",
    descriptionKey: "modifyContract",
    requiresApproval: true,
    approvalLevelKey: "requiresLegal",
  },
  {
    id: "rotation",
    labelKey: "jobRotation",
    icon: Repeat,
    color: "linear-gradient(135deg, #ec4899, #db2777)",
    bgColor: "rgba(236, 72, 153, 0.15)",
    borderColor: "rgba(236, 72, 153, 0.4)",
    descriptionKey: "rotateDifferent",
    requiresApproval: true,
    approvalLevelKey: "requiresDeptHead",
  },
  {
    id: "review",
    labelKey: "performanceReview",
    icon: ClipboardList,
    color: "linear-gradient(135deg, #14b8a6, #0d9488)",
    bgColor: "rgba(20, 184, 166, 0.15)",
    borderColor: "rgba(20, 184, 166, 0.4)",
    descriptionKey: "conductEvaluation",
    requiresApproval: false,
    approvalLevelKey: "requiresDirectManager",
  },
  {
    id: "training",
    labelKey: "trainingAssignment",
    icon: GraduationCap,
    color: "linear-gradient(135deg, #6366f1, #4f46e5)",
    bgColor: "rgba(99, 102, 241, 0.15)",
    borderColor: "rgba(99, 102, 241, 0.4)",
    descriptionKey: "assignTraining",
    requiresApproval: false,
    approvalLevelKey: "requiresTrainingManager",
  },
];

// Map action ids to admin-level feature keys
const ACTION_FEATURE_MAP = {
  promote: "promoteEmployee",
  demote: "demoteEmployee",
  transfer: "transferEmployee",
  salary_change: "salaryAdjustment",
  terminate: "terminateEmployment",
  suspend: "suspendEmployee",
  rehire: "rehireEmployee",
  contract: "contractUpdate",
  rotation: "jobRotation",
  review: "performanceReview",
  training: "trainingAssignment",
};

// ===================================
// MAIN COMPONENT
// ===================================
const EmployeeActionsPage = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();
  const t = translations[language] || translations.ar;
  const isRTL = language === "ar";

  // MEMOIZE STYLES
  const styles = useMemo(() => createStyles(theme, isRTL), [theme, isRTL]);

  // State Management
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("all");
  const [filterPerformance, setFilterPerformance] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [actionHistory, setActionHistory] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [permissions, setPermissions] = useState({ modules: [], features: {} });
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [userRole, setUserRole] = useState(profile?.role || localStorage.getItem("role") || "");
  const viewType = localStorage.getItem("managerViewType") || localStorage.getItem("viewType") || "manager";
  const isAdminView = viewType === "admin";
  const isAdminRole = userRole === "admin" || userRole === "superadmin";

  // Fetch resolved permissions for current user when in Admin view
  useEffect(() => {
    setUserRole(profile?.role || localStorage.getItem("role") || "");
    if (!isAdminView) return;

    const fetchPermissions = async () => {
      try {
        setPermissionsLoading(true);
        const client = profile?.workingCompany || localStorage.getItem("workingCompany") || "";
        if (!client) {
          setPermissions({ modules: [], features: {} });
          return;
        }

        const BASE = process.env.REACT_APP_BASE_URL || "";
        const url = `${BASE}/api/admin-levels/resolve/permissions?workingCompany=${encodeURIComponent(client)}`;
        const res = await apiFetch(url, { method: 'GET' });
        if (!res.ok) {
          console.warn("Failed to fetch admin-level permissions:", res.status);
          setPermissions({ modules: [], features: {} });
          return;
        }
        const json = await res.json();
        // Backend may return { success, data } or { permissions }
        const payload = json.permissions || json.data || json;
        // Normalize to { modules: [], features: { moduleName: [featureKeys...] } }
        setPermissions(payload || { modules: [], features: {} });
      } catch (err) {
        console.error("Error fetching permissions:", err);
        setPermissions({ modules: [], features: {} });
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchPermissions();
  }, [isAdminView]);
  const [pendingWorkflows, setPendingWorkflows] = useState({}); // map actionId -> workflow array
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    promotionsThisMonth: 0,
    transfersThisMonth: 0,
    avgSalaryIncrease: 0,
    pendingApprovals: 0,
    completedActions: 0,
  });

  // Modal States
  const [showActionModal, setShowActionModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [hoveredActionBtn, setHoveredActionBtn] = useState(null);

  // Selected Data
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [selectedAction, setSelectedAction] = useState("");
  const [pendingAction, setPendingAction] = useState(null);

  // UI States
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showEmployeeHistoryModal, setShowEmployeeHistoryModal] =
    useState(false);
  const [selectedEmployeeHistory, setSelectedEmployeeHistory] = useState(null);
  const [employeeHistoryData, setEmployeeHistoryData] = useState([]);
  const [employeeImages, setEmployeeImages] = useState({}); // Store loaded images
  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingActionId, setRejectingActionId] = useState(null);
  const [rejectingActionData, setRejectingActionData] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  // Form Data
  const [formData, setFormData] = useState({
    newPosition: "",
    newDepartment: "",
    newSalary: "",
    effectiveDate: "",
    reason: "",
    approverNotes: "",
    reportsTo: "",
    performanceRating: "",
    terminationType: "",
    terminationReason: "",
    lastWorkingDay: "",
    severanceAmount: "",
    eligibleForRehire: "yes",
    suspensionStartDate: "",
    suspensionEndDate: "",
    suspensionType: "",
    suspensionReason: "",
    trainingProgram: "",
    trainingStartDate: "",
    trainingEndDate: "",
    trainingCost: "",
    trainingObjectives: "",
    reviewScore: "",
    strengths: "",
    improvements: "",
    nextReviewDate: "",
    contractType: "",
    contractStartDate: "",
    contractEndDate: "",
    contractTerms: "",
    rotationDepartment: "",
    rotationRole: "",
    rotationDuration: "",
    rotationObjectives: "",
    priority: "medium",
    confidential: false,
    notifyEmployee: true,
    notifyManager: true,
    attachments: [],
  });

  // Refs
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // API Integration Functions
  const fetchClients = useCallback(async () => {
    try {
      const data = await employeeActionsAPI.getAllClients();
      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClients([]);
    }
  }, []);

  const fetchActionHistory = useCallback(async () => {
    try {
      const data = await employeeActionsAPI.getAllActions();
      setActionHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching action history:", error);
      setActionHistory([]);
    }
  }, []);

  const fetchPendingActions = useCallback(async () => {
    try {
      const viewRole = isAdminView ? "admin" : "manager";

      // Build options only from available values; do not use clientName
      const options = {};
      if (selectedClient && selectedClient !== "all") options.clientIdentifier = selectedClient.toLowerCase();
      const workingCompany = profile?.workingCompany || localStorage.getItem("workingCompany");
      if (workingCompany) options.workingCompany = workingCompany;

      // resolve adminLevelId if available
      const adminLevelId = profile?.adminLevelId;
      if (adminLevelId) {
        options.adminLevelId = adminLevelId;
      } else {
        try {
          const userStr = localStorage.getItem("user") || sessionStorage.getItem("user");
          if (userStr) {
            const userObj = JSON.parse(userStr);
            if (userObj?.adminLevelId || userObj?.admin_level_id) options.adminLevelId = userObj.adminLevelId || userObj.admin_level_id;
          }
        } catch (e) {
          // ignore
        }
      }

      // manager infinity code if available
      const mgr = profile?.infinityCode || localStorage.getItem("infinityCode");
      if (mgr) options.managerInfinityCode = mgr;

      const data = await employeeActionsAPI.getPendingActionsByView(viewRole, options);

      const pendingData = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : data?.actions
            ? data.actions
            : [];

      setPendingActions(pendingData);

      // Fetch workflows for pending actions so we can determine approver permissions
      try {
        const workflowPromises = pendingData.map(async (action) => {
          const wf = await employeeActionsAPI.getActionWorkflow(action.id);
          return { id: action.id, workflow: Array.isArray(wf) ? wf : wf.data || wf };
        });

        const workflows = await Promise.all(workflowPromises);
        const wfMap = {};
        workflows.forEach((w) => {
          wfMap[w.id] = w.workflow || [];
        });
        setPendingWorkflows(wfMap);
      } catch (e) {
        console.warn("Could not fetch workflows for pending actions", e);
        setPendingWorkflows({});
      }

      setStats((prev) => ({
        ...prev,
        pendingApprovals: pendingData.length,
      }));

      return pendingData;
    } catch (error) {
      console.error("Error fetching pending actions:", error);
      setPendingActions([]);
      return [];
    }
  }, [isAdminView, selectedClient]);

  const fetchEmployees = useCallback(
    async (showLoader = false, page = 1) => {
      if (showLoader) setIsLoading(true);
      try {
        const workingCompany = profile?.workingCompany || localStorage.getItem("workingCompany") || undefined;
        const storedClientName = profile?.clientName || localStorage.getItem("clientName") || undefined;
        const filters = {
          department: filterDepartment !== "all" ? filterDepartment : undefined,
          performance:
            filterPerformance !== "all" ? filterPerformance : undefined,
          searchTerm: searchTerm || undefined,
          // do not use clientName; prefer workingCompany when present
          workingCompany,
        };

        let data;

        // Get view type and user info
        const userRole = (profile?.role || localStorage.getItem("role") || "").toLowerCase();
        const myInfinityCode = profile?.infinityCode || localStorage.getItem("infinityCode");
        const storedViewType = (localStorage.getItem("managerViewType") || localStorage.getItem("viewType") || "manager").toLowerCase();
        const forceAdminView = userRole === "admin" || userRole === "superadmin";
        const resolvedViewType = forceAdminView ? "admin" : storedViewType;

        const resolveSelectedClientName = () => {
          if (!selectedClient || selectedClient === "all") {
            return storedClientName || workingCompany;
          }

          const selectedValue = selectedClient.toString().toLowerCase();
          const match = clients.find((client) => {
            if (!client) return false;
            const idStr = client.id !== undefined && client.id !== null ? client.id.toString().toLowerCase() : null;
            const nameStr = (client.clientName || client.name || "").toString().toLowerCase();
            return (idStr && idStr === selectedValue) || (nameStr && nameStr === selectedValue);
          });

          if (match) {
            return match.clientName || match.name || selectedClient;
          }

          return selectedClient;
        };

        const resolvedClientName = resolveSelectedClientName();

        console.log(
          `[changesPage] Fetching employees - viewType: ${resolvedViewType}, userRole: ${userRole}, client=${resolvedClientName}`
        );

        if (resolvedViewType === "admin") {
          console.log("[changesPage] Using ADMIN view - visibility rules");
          const company = resolvedClientName || workingCompany || storedClientName;
          data = company
            ? await employeeActionsAPI.getEmployeesByVisibility(company, "admin")
            : await employeeActionsAPI.getEmployeesByVisibility(undefined, "admin");
        } else {
          console.log("[changesPage] Using MANAGER view - hierarchy");

          const targetClient = resolvedClientName || workingCompany || storedClientName;

          if (!targetClient) {
            throw new Error("No client identifier available to fetch employees");
          }

          if (!myInfinityCode) {
            if (forceAdminView) {
              console.warn(
                "[changesPage] Missing manager infinity code; falling back to admin visibility fetch"
              );
              data = await employeeActionsAPI.getEmployeesByVisibility(targetClient, "admin");
            } else {
              throw new Error("No manager infinity code available for hierarchy fetch");
            }
          } else {
            const clientIdentifier = targetClient.toString().trim();
            const managerClientIdentifier =
              selectedClient && selectedClient !== "all"
                ? clientIdentifier.toLowerCase()
                : clientIdentifier;

            data = await employeeActionsAPI.getEmployeesByClientbymanager(
              managerClientIdentifier,
              myInfinityCode
            );

            if ((!Array.isArray(data) || data.length === 0) && forceAdminView) {
              console.log(
                "[changesPage] Manager view returned no employees for admin role; falling back to admin visibility"
              );
              data = await employeeActionsAPI.getEmployeesByVisibility(targetClient, "admin");
            }
          }
        }

        const allEmployees = Array.isArray(data) ? data : [];
        setEmployees(allEmployees);
      } catch (error) {
        console.error("Error fetching employees:", error);
        setErrorMsg(t.errorLoadingEmployees);
        setEmployees([]);
      } finally {
        if (showLoader) setIsLoading(false);
      }
    },
    [
      selectedClient,
      filterDepartment,
      filterPerformance,
      searchTerm,
      t,
      clients,
    ]
  );


  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue =
            language === "ar"
              ? (a.arabicName || a.sr || "").toLowerCase()
              : (a.sr || a.arabicName || "").toLowerCase();
          bValue =
            language === "ar"
              ? (b.arabicName || b.sr || "").toLowerCase()
              : (b.sr || b.arabicName || "").toLowerCase();
          break;
        case "salary":
          aValue = a.netSalary || 0;
          bValue = b.netSalary || 0;
          break;
        case "joinDate":
          aValue = new Date(a.hiringDate || 0);
          bValue = new Date(b.hiringDate || 0);
          break;
        case "performance":
          const perfOrder = {
            Excellent: 4,
            "Very Good": 3,
            Good: 2,
            Average: 1,
          };
          aValue = perfOrder[a.performance] || 0;
          bValue = perfOrder[b.performance] || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [employees, sortBy, sortOrder, language]);

  const filteredEmployees = useMemo(() => {
    return sortedEmployees.filter((emp) => {
      const searchName =
        language === "ar"
          ? emp.arabicName || emp.sr || ""
          : emp.sr || emp.arabicName || "";

      const matchesSearch =
        !searchTerm ||
        searchName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.employeeType || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (emp.infinityCode || "").toString().includes(searchTerm);

      const matchesDept =
        filterDepartment === "all" || emp.employeeType === filterDepartment;

      const matchesPerf =
        filterPerformance === "all" || emp.performance === filterPerformance;

      const matchesStatus =
        filterStatus === "all" || emp.employeeState === filterStatus;

      return matchesSearch && matchesDept && matchesPerf && matchesStatus;
    });
  }, [
    sortedEmployees,
    searchTerm,
    filterDepartment,
    filterPerformance,
    filterStatus,
    language,
  ]);

  const fetchStats = useCallback(async () => {
    try {
      const totalCount = employees.length;
      const activeCount = employees.filter(
        (emp) =>
          emp.employeeState === "Active" || emp.employeeState === "active"
      ).length;

      const actionsArray = Array.isArray(actionHistory)
        ? actionHistory
        : Array.isArray(actionHistory?.data)
          ? actionHistory.data
          : [];

      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();

      const monthlyActions = actionsArray.filter((action) => {
        const actionDate = new Date(
          action.effectiveDate || action.createdAt || action.requestedDate
        );
        return (
          actionDate.getMonth() === currentMonth &&
          actionDate.getFullYear() === currentYear
        );
      });

      const promotions = monthlyActions.filter(
        (action) => action.actionType === "promote"
      ).length;
      const transfers = monthlyActions.filter(
        (action) => action.actionType === "transfer"
      ).length;

      const salaryActions = monthlyActions.filter(
        (action) => action.changePercent && action.changePercent > 0
      );
      const avgSalaryIncrease =
        salaryActions.length > 0
          ? salaryActions.reduce(
            (sum, action) => sum + action.changePercent,
            0
          ) / salaryActions.length
          : 0;

      setStats({
        totalEmployees: totalCount,
        activeEmployees: activeCount,
        promotionsThisMonth: promotions,
        transfersThisMonth: transfers,
        avgSalaryIncrease: avgSalaryIncrease.toFixed(1),
        pendingApprovals: pendingActions.length,
        completedActions: monthlyActions.length,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      setStats({
        totalEmployees: 0,
        activeEmployees: 0,
        promotionsThisMonth: 0,
        transfersThisMonth: 0,
        avgSalaryIncrease: 0,
        pendingApprovals: 0,
        completedActions: 0,
      });
    }
  }, [employees, pendingActions.length, actionHistory]);

  const handleApproveAction = useCallback(
    async (actionId) => {
      setIsSubmitting(true);
      try {
        const approvalData = {
          approvedBy: profile?.infinityCode || localStorage.getItem("infinityCode"),
          comments: formData.comments,
        };
        await employeeActionsAPI.approveAction(actionId, approvalData);
        setSuccessMsg(t.successActionApproved);

        await Promise.all([
          fetchPendingActions(),
          fetchActionHistory(),
          fetchStats(),
        ]);

        setTimeout(() => setSuccessMsg(""), 5000);
      } catch (error) {
        console.error("Error approving action:", error);
        setErrorMsg(t.errorApprovingAction);
        setTimeout(() => setErrorMsg(""), 5000);
      } finally {
        setIsSubmitting(false);
      }
    },
    [fetchPendingActions, fetchActionHistory, fetchStats, t]
  );

  const handleRejectAction = useCallback(
    async (actionId, reasonOverride = null) => {
      setIsSubmitting(true);
      try {
        // Use the override if provided, otherwise fall back to state
        const reasonText = (reasonOverride && reasonOverride.trim()) || rejectionReason?.trim() || formData.approverNotes || formData.comments || "Rejected by approver";
        const rejectData = {
          rejectedBy: profile?.infinityCode || localStorage.getItem("infinityCode"),
          rejectionReason: reasonText,
          reason: reasonText,
          comments: reasonText,
        };

        await employeeActionsAPI.rejectAction(actionId, rejectData);
        setSuccessMsg(t.successActionRejected);

        // Close modal state if used
        setShowRejectModal(false);
        setRejectingActionId(null);
        setRejectingActionData(null);
        setRejectionReason("");

        await Promise.all([fetchPendingActions(), fetchActionHistory(), fetchStats()]);

        setTimeout(() => setSuccessMsg(""), 5000);
      } catch (error) {
        console.error("Error rejecting action:", error);
        setErrorMsg(t.errorRejectingAction);
        setTimeout(() => setErrorMsg(""), 5000);
      } finally {
        setIsSubmitting(false);
      }
    },
    [rejectionReason, fetchPendingActions, fetchActionHistory, fetchStats, t, formData]
  );

  const handleOpenPendingModal = useCallback(async () => {
    setShowPendingModal(true);
    await fetchPendingActions();
  }, [fetchPendingActions]);

  // Initial Data Loading
  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      try {
        await Promise.all([
          fetchClients(),
          fetchEmployees(true),
          fetchActionHistory(),
          fetchPendingActions(),
        ]);
        // Note: Don't call fetchStats() here - it will be called automatically
        // by the useEffect that watches employees.length changes
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Load employee images with authentication
  useEffect(() => {
    const loadEmployeeImages = async () => {
      const token = localStorage.getItem("token");
      const BASE_URL = process.env.REACT_APP_BASE_URL || "";

      for (const emp of employees) {
        if (!emp.infinityCode || employeeImages[emp.infinityCode]) continue;

        try {
          const imageUrl = `${BASE_URL}/employees/pic/${emp.infinityCode}`;
          const response = await apiFetch(imageUrl, { method: 'GET' });

          if (response.ok) {
            const blob = await response.blob();
            const objectURL = URL.createObjectURL(blob);
            setEmployeeImages(prev => ({
              ...prev,
              [emp.infinityCode]: objectURL
            }));
          }
        } catch (error) {
          // Image failed to load - will show initials
          console.debug(`Could not load image for employee ${emp.infinityCode}`);
        }
      }
    };

    if (employees.length > 0) {
      loadEmployeeImages();
    }

    // Cleanup object URLs on unmount
    return () => {
      Object.values(employeeImages).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [employees]);

  // DEBOUNCED filter changes
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Reset filters and reload full list (no pagination)
      fetchEmployees(false);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    selectedClient,
    filterDepartment,
    filterPerformance,
    searchTerm,
    refreshTrigger,
    fetchEmployees,
  ]);

  useEffect(() => {
    if (!isLoading && employees.length > 0) {
      fetchStats();
    }
  }, [filteredEmployees, fetchStats, isLoading, employees.length]);

  useEffect(() => {
    const target = window.__notificationTarget;
    if (target?.type === "EMPLOYEE_ACTION" && pendingActions.length > 0) {
      setShowPendingModal(true);
      window.__notificationTarget = null;
    }
  }, [pendingActions]);

  // Callback handlers
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrorMsg("");
  }, []);

  // Handler for ReusableDropdown
  const handleDropdownChange = useCallback((fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    setErrorMsg("");
  }, []);

  const handleFileUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }));
  }, []);

  const openActionModal = useCallback((employee) => {
    setSelectedEmployee(employee);
    setSelectedAction("");
    setIsDropdownOpen(false);
    setFormData({
      newPosition: employee.employeeType || "",
      newDepartment: employee.employeeType || "",
      newSalary: employee.netSalary || "",
      effectiveDate: new Date().toISOString().split("T")[0],
      reason: "",
      approverNotes: "",
      reportsTo: employee.firstLineManagerName || "",
      performanceRating: "",
      suspensionEndDate: "",
      terminationReason: "",
      trainingProgram: "",
      reviewScore: "",
      contractType: "",
      rotationDepartment: "",
      priority: "medium",
      confidential: false,
      notifyEmployee: true,
      notifyManager: true,
      attachments: [],
    });
    setErrorMsg("");
    setShowActionModal(true);
  }, []);

  const validateForm = useCallback(() => {
    if (!selectedAction) {
      setErrorMsg(t.pleaseSelectAction);
      return false;
    }
    if (!formData.effectiveDate) {
      setErrorMsg(t.effectiveDateRequired);
      return false;
    }
    if (!formData.reason) {
      setErrorMsg(t.reasonRequired);
      return false;
    }
    if (!formData.approverNotes) {
      setErrorMsg(t.approverNotesRequired);
      return false;
    }
    return true;
  }, [
    selectedAction,
    formData.effectiveDate,
    formData.reason,
    formData.approverNotes,
    t,
  ]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateForm()) return;

      const actionConfig = ACTION_TYPES.find((a) => a.id === selectedAction);

      setPendingAction({
        type: selectedAction,
        employee: selectedEmployee,
        formData,
        actionConfig,
      });

      setShowActionModal(false);
      setShowConfirmModal(true);
    },
    [selectedAction, selectedEmployee, formData, validateForm]
  );

  const handleConfirmAction = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const isSalaryRelatedAction = [
        "salary_change",
        "promote",
        "demote",
        "rehire",
      ].includes(pendingAction.type);

      const oldSalary = selectedEmployee.netSalary || 0;
      const newSalary = formData.newSalary
        ? parseFloat(formData.newSalary)
        : oldSalary;
      const salaryChange = newSalary - oldSalary;
      const changePercent =
        oldSalary > 0 ? (salaryChange / oldSalary) * 100 : 0;

      const actionData = {
        employeeId: selectedEmployee.id,
        infinityCode: selectedEmployee.infinityCode,
        employeeName:
          language === "ar"
            ? selectedEmployee.arabicName || selectedEmployee.sr
            : selectedEmployee.sr || selectedEmployee.arabicName,
        actionType: pendingAction.type,
        effectiveDate: formData.effectiveDate,
        reason: formData.reason,
        approverNotes: formData.approverNotes,
        oldPosition: selectedEmployee.employeeType,
        newPosition: formData.newPosition || selectedEmployee.employeeType,
        oldDepartment: selectedEmployee.employeeType,
        newDepartment: formData.newDepartment || selectedEmployee.employeeType,
        oldSalary: oldSalary,
        newSalary: isSalaryRelatedAction ? newSalary : oldSalary,
        salaryChange: isSalaryRelatedAction ? salaryChange : 0,
        changePercent: isSalaryRelatedAction ? changePercent : 0,
        oldReportsTo: selectedEmployee.firstLineManagerName,
        newReportsTo:
          formData.reportsTo || selectedEmployee.firstLineManagerName,
        performanceRating: formData.performanceRating,
        benefits: formData.benefits,
        terminationType: formData.terminationType,
        terminationReason: formData.terminationReason,
        lastWorkingDay: formData.lastWorkingDay,
        severanceAmount: formData.severanceAmount,
        eligibleForRehire: formData.eligibleForRehire,
        suspensionStartDate: formData.suspensionStartDate,
        suspensionEndDate: formData.suspensionEndDate,
        suspensionType: formData.suspensionType,
        suspensionReason: formData.suspensionReason,
        trainingProgram: formData.trainingProgram,
        trainingStartDate: formData.trainingStartDate,
        trainingEndDate: formData.trainingEndDate,
        trainingCost: formData.trainingCost,
        trainingObjectives: formData.trainingObjectives,
        reviewScore: formData.reviewScore,
        strengths: formData.strengths,
        improvements: formData.improvements,
        nextReviewDate: formData.nextReviewDate,
        contractType: formData.contractType,
        contractStartDate: formData.contractStartDate,
        contractEndDate: formData.contractEndDate,
        contractTerms: formData.contractTerms,
        rotationDepartment: formData.rotationDepartment,
        rotationRole: formData.rotationRole,
        rotationDuration: formData.rotationDuration,
        rotationObjectives: formData.rotationObjectives,
        performedBy: String(profile?.infinityCode || localStorage.getItem("infinityCode")),
        status: pendingAction.actionConfig.requiresApproval
          ? "pending"
          : "approved",
        priority: formData.priority || "medium",
        confidential: formData.confidential || false,
        oldData: {
          position: selectedEmployee.employeeType,
          department: selectedEmployee.employeeType,
          salary: oldSalary,
          reportsTo: selectedEmployee.firstLineManagerName,
          performance: selectedEmployee.performance,
        },
        newData: {
          position: formData.newPosition || selectedEmployee.employeeType,
          department: formData.newDepartment || selectedEmployee.employeeType,
          salary: newSalary,
          reportsTo:
            formData.reportsTo || selectedEmployee.firstLineManagerName,
          performance:
            formData.performanceRating || selectedEmployee.performance,
        },
      };

      const response = await employeeActionsAPI.createAction(actionData);

      setRefreshTrigger((prev) => prev + 1);
      await Promise.all([
        fetchEmployees(false),
        fetchActionHistory(),
        fetchPendingActions(),
        fetchStats(),
      ]);

      const actionLabel =
        t[pendingAction.actionConfig.labelKey] ||
        pendingAction.actionConfig.labelKey;
      const employeeName =
        language === "ar"
          ? selectedEmployee.arabicName || selectedEmployee.sr
          : selectedEmployee.sr || selectedEmployee.arabicName;

      let successMessage = `${actionLabel} ${t.actionSubmittedSuccess} ${employeeName}.`;

      if (isSalaryRelatedAction && salaryChange !== 0) {
        const salaryChangeText =
          salaryChange > 0
            ? `${t.salaryWillIncrease} ${Math.abs(
              salaryChange
            ).toLocaleString()} (+${changePercent.toFixed(1)}%)`
            : `${t.salaryWillDecrease} ${Math.abs(
              salaryChange
            ).toLocaleString()} (${changePercent.toFixed(1)}%)`;

        successMessage += ` ${salaryChangeText}.`;

        if (pendingAction.actionConfig.requiresApproval) {
          successMessage += ` ${t.changesAppliedOnApproval}`;
        } else {
          successMessage += ` ${t.changesAppliedImmediately}`;
        }
      } else if (pendingAction.actionConfig.requiresApproval) {
        successMessage += ` ${t.awaitingApproval}`;
      }

      setSuccessMsg(successMessage);

      setShowConfirmModal(false);
      setPendingAction(null);
      setSelectedEmployee(null);
      setSelectedAction("");
      setFormData({
        newPosition: "",
        newDepartment: "",
        newSalary: "",
        effectiveDate: "",
        reason: "",
        approverNotes: "",
        reportsTo: "",
        performanceRating: "",
        terminationType: "",
        terminationReason: "",
        lastWorkingDay: "",
        severanceAmount: "",
        eligibleForRehire: "yes",
        suspensionStartDate: "",
        suspensionEndDate: "",
        suspensionType: "",
        suspensionReason: "",
        trainingProgram: "",
        trainingStartDate: "",
        trainingEndDate: "",
        trainingCost: "",
        trainingObjectives: "",
        reviewScore: "",
        strengths: "",
        improvements: "",
        nextReviewDate: "",
        contractType: "",
        contractStartDate: "",
        contractEndDate: "",
        contractTerms: "",
        rotationDepartment: "",
        rotationRole: "",
        rotationDuration: "",
        rotationObjectives: "",
        priority: "medium",
        confidential: false,
        notifyEmployee: true,
        notifyManager: true,
        attachments: [],
      });

      setTimeout(() => setSuccessMsg(""), 7000);
    } catch (error) {
      console.error("Error creating action:", error);

      let errorMessage = t.errorProcessingAction + " ";
      if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += t.pleaseTryAgain;
      }

      setErrorMsg(errorMessage);
      setTimeout(() => setErrorMsg(""), 10000);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedEmployee,
    pendingAction,
    formData,
    fetchEmployees,
    fetchActionHistory,
    fetchPendingActions,
    fetchStats,
    t,
    language,
  ]);

  const handleOpenEmployeeHistory = useCallback(async (employee) => {
    setSelectedEmployeeHistory(employee);
    setShowEmployeeHistoryModal(true);

    try {
      const history = await employeeActionsAPI.getEmployeeActions(
        employee.infinityCode
      );
      const sortedHistory = Array.isArray(history)
        ? history.sort(
          (a, b) =>
            new Date(b.effectiveDate || b.createdAt) -
            new Date(a.effectiveDate || a.createdAt)
        )
        : [];
      setEmployeeHistoryData(sortedHistory);
    } catch (error) {
      console.error("Error fetching employee history:", error);
      setEmployeeHistoryData([]);
    }
  }, []);

  // Utility Functions - Uses shared English-only initials (NEVER Arabic)
  const getInitials = useCallback((name, englishName = null) => {
    // Always prefer English name for initials
    return getEnglishInitials(name, englishName, "US");
  }, []);

  const getEmployeeName = useCallback(
    (emp) => {
      return language === "ar"
        ? emp.arabicName || emp.sr || t.notAvailable
        : emp.sr || emp.arabicName || t.notAvailable;
    },
    [language, t]
  );

  const departments = useMemo(
    () => [...new Set(employees.map((e) => e.employeeType).filter(Boolean))],
    [employees]
  );

  const performances = useMemo(
    () => [...new Set(employees.map((e) => e.performance).filter(Boolean))],
    [employees]
  );

  const statuses = useMemo(
    () => [...new Set(employees.map((e) => e.employeeState).filter(Boolean))],
    [employees]
  );

  // Custom Dropdown Component
  const CustomActionDropdown = useMemo(() => {
    const selectedActionData = ACTION_TYPES.find(
      (a) => a.id === selectedAction
    );
    const ActionIcon = selectedActionData?.icon || Sparkles;

    return (
      <div style={styles.customDropdown}>
        <div
          style={{
            ...styles.dropdownTrigger,
            borderColor: selectedAction
              ? selectedActionData.borderColor
              : theme === "dark"
                ? "rgba(255, 255, 255, 0.18)"
                : "rgba(0, 0, 0, 0.1)",
          }}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="enhanced-input-focus"
        >
          <div style={styles.dropdownTriggerContent}>
            <div
              style={{
                ...styles.dropdownIconWrapper,
                background: selectedAction
                  ? selectedActionData.color
                  : "linear-gradient(135deg, #94a3b8, #64748b)",
              }}
            >
              <ActionIcon size={22} color="white" />
            </div>
            <div style={styles.dropdownTextWrapper}>
              <div style={styles.dropdownLabel}>
                {selectedAction
                  ? t[selectedActionData.labelKey]
                  : t.selectActionType}
              </div>
              <div style={styles.dropdownDescription}>
                {selectedAction
                  ? t[selectedActionData.descriptionKey]
                  : t.chooseAction}
              </div>
            </div>
          </div>
          <ChevronDown
            size={22}
            style={{
              ...styles.dropdownChevron,
              ...(isDropdownOpen ? styles.dropdownChevronOpen : {}),
            }}
          />
        </div>

        {isDropdownOpen && (
          <div style={styles.dropdownMenu}>
            {ACTION_TYPES
              .filter((action) => {
                // If not in admin view or user is admin role, allow all actions
                if (!isAdminView || isAdminRole) return true;
                // Otherwise check permissions
                const featureKey = ACTION_FEATURE_MAP[action.id] || null;
                if (!featureKey) return true;
                const modules = permissions?.modules || [];
                const featuresForModule = permissions?.features?.changesPage || permissions?.features?.changes || [];
                if (!modules.includes("changesPage") && !modules.includes("changes")) return false;
                return Array.isArray(featuresForModule) ? featuresForModule.includes(featureKey) : false;
              })
              .map((action) => {
                const ItemIcon = action.icon;
                const isSelected = selectedAction === action.id;
                return (
                  <div
                    key={action.id}
                    style={{
                      ...styles.dropdownMenuItem,
                      ...(isSelected ? styles.dropdownMenuItemSelected : {}),
                    }}
                    onClick={() => {
                      setSelectedAction(action.id);
                      setIsDropdownOpen(false);
                      setErrorMsg("");
                    }}
                  >
                    <div
                      style={{
                        ...styles.dropdownIconWrapper,
                        background: action.color,
                        width: "clamp(36px, 5.5vw, 42px)",
                        height: "clamp(36px, 5.5vw, 42px)",
                      }}
                    >
                      <ItemIcon size={19} color="white" />
                    </div>
                    <div style={{ ...styles.dropdownTextWrapper, flex: 1 }}>
                      <div
                        style={{
                          ...styles.dropdownLabel,
                          fontSize: "clamp(13px, 1.9vw, 15px)",
                        }}
                      >
                        {t[action.labelKey]}
                      </div>
                      <div style={styles.dropdownDescription}>
                        {t[action.descriptionKey]}
                      </div>
                      <div
                        style={{
                          fontSize: "clamp(10px, 1.4vw, 11px)",
                          color:
                            theme === "dark"
                              ? "rgba(255,255,255,0.5)"
                              : "#94a3b8",
                          marginTop: "2px",
                        }}
                      >
                        {t[action.approvalLevelKey]}
                      </div>
                    </div>
                    {isSelected && (
                      <Check
                        size={22}
                        style={{ color: "#2e7cba", flexShrink: 0 }}
                        strokeWidth={3}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  }, [selectedAction, isDropdownOpen, styles, theme, t]);

  // Render form fields based on selected action
  const renderFormFields = useCallback(() => {
    if (!selectedAction) {
      return (
        <div style={styles.emptyState}>
          <div style={styles.emptyStateIcon}>
            <Target size={60} />
          </div>
          <h3 style={styles.emptyStateTitle}>{t.selectAction}</h3>
          <p style={styles.emptyStateText}>{t.selectActionToProceed}</p>
        </div>
      );
    }

    const actionConfig = ACTION_TYPES.find((a) => a.id === selectedAction);

    const renderActionSpecificFields = () => {
      switch (selectedAction) {
        case "promote":
          return (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Briefcase size={14} />
                  {t.newPosition} *
                </label>
                <input
                  type="text"
                  name="newPosition"
                  value={formData.newPosition}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterNewPosition}
                  required
                />
              </div>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <DollarSign size={14} />
                  {t.newSalary} *
                </label>
                <input
                  type="number"
                  name="newSalary"
                  value={formData.newSalary}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterNewSalary}
                  required
                  min="0"
                  step="100"
                />
                {selectedEmployee && formData.newSalary && (
                  <div
                    style={{
                      fontSize: "clamp(11px, 1.6vw, 12px)",
                      color:
                        theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                      marginTop: "6px",
                      fontWeight: "600",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {t.currentSalary}: {t.egp}{" "}
                    {selectedEmployee.netSalary?.toLocaleString()} →{" "}
                    {t.newSalaryAmount}: {t.egp}{" "}
                    {parseFloat(formData.newSalary).toLocaleString()}
                    {formData.newSalary > selectedEmployee.netSalary && (
                      <span
                        style={{
                          color: "#10b981",
                          marginLeft: isRTL ? "0" : "8px",
                          marginRight: isRTL ? "8px" : "0",
                        }}
                      >
                        (+
                        {(
                          ((formData.newSalary - selectedEmployee.netSalary) /
                            selectedEmployee.netSalary) *
                          100
                        ).toFixed(1)}
                        %)
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Building size={14} />
                  {t.newDepartment}
                </label>
                <ReusableDropdown
                  items={departments.map((dept) => ({ id: dept, name: dept }))}
                  value={formData.newDepartment}
                  onChange={(value) => handleDropdownChange("newDepartment", value)}
                  placeholder={t.keepCurrentDept}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("newDepartment", "")}
                />
              </div>
            </>
          );

        case "demote":
          return (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Briefcase size={14} />
                  {t.newPosition} *
                </label>
                <input
                  type="text"
                  name="newPosition"
                  value={formData.newPosition}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterNewPosition}
                  required
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <DollarSign size={14} />
                  {t.newSalary} *
                </label>
                <input
                  type="number"
                  name="newSalary"
                  value={formData.newSalary}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterNewSalary}
                  required
                  min="0"
                  step="100"
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <AlertCircle size={14} />
                  {t.demotionReasonCategory} *
                </label>
                <ReusableDropdown
                  items={[
                    { id: '', name: t.selectReason },
                    { id: 'performance', name: t.performanceIssues },
                    { id: 'restructuring', name: t.companyRestructuring },
                    { id: 'misconduct', name: t.misconduct },
                    { id: 'business_needs', name: t.businessNeeds },
                    { id: 'other', name: t.other }
                  ]}
                  value={formData.demotionReason}
                  onChange={(value) => handleDropdownChange("demotionReason", value)}
                  placeholder={t.selectReason}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("demotionReason", "")}
                />
              </div>
            </>
          );

        case "transfer":
          return (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Building size={14} />
                  {t.newDepartment} *
                </label>
                <ReusableDropdown
                  items={departments.map((dept) => ({ id: dept, name: dept }))}
                  value={formData.newDepartment}
                  onChange={(value) => handleDropdownChange("newDepartment", value)}
                  placeholder={t.selectDepartment}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("newDepartment", "")}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Briefcase size={14} />
                  {t.newPosition}
                </label>
                <input
                  type="text"
                  name="newPosition"
                  value={formData.newPosition}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.keepCurrentPosition}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Users size={14} />
                  {t.newManager}
                </label>
                <ManagerAutocomplete
                  value={formData.reportsTo}
                  onChange={handleInputChange}
                  theme={theme}
                  styles={styles}
                  language={language}
                  t={t}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <MapPin size={14} />
                  {t.transferType} *
                </label>
                <ReusableDropdown
                  items={[
                    { id: '', name: t.selectType },
                    { id: 'permanent', name: t.permanentTransfer },
                    { id: 'temporary', name: t.temporaryTransfer },
                    { id: 'project_based', name: t.projectBased }
                  ]}
                  value={formData.transferType}
                  onChange={(value) => handleDropdownChange("transferType", value)}
                  placeholder={t.selectType}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("transferType", "")}
                />
              </div>
            </>
          );

        case "salary_change":
          return (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <DollarSign size={14} />
                  {t.newSalary} *
                </label>
                <input
                  type="number"
                  name="newSalary"
                  value={formData.newSalary}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterNewSalary}
                  required
                  min="0"
                  step="100"
                />
                {selectedEmployee && formData.newSalary && (
                  <div
                    style={{
                      fontSize: "clamp(11px, 1.6vw, 12px)",
                      color:
                        theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                      marginTop: "6px",
                      fontWeight: "600",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {t.currentSalary}: {t.egp}{" "}
                    {selectedEmployee.netSalary?.toLocaleString()} →{" "}
                    {t.newSalaryAmount}: {t.egp}{" "}
                    {parseFloat(formData.newSalary).toLocaleString()}
                    <span
                      style={{
                        color:
                          formData.newSalary > selectedEmployee.netSalary
                            ? "#10b981"
                            : "#ef4444",
                        marginLeft: isRTL ? "0" : "8px",
                        marginRight: isRTL ? "8px" : "0",
                      }}
                    >
                      (
                      {formData.newSalary > selectedEmployee.netSalary
                        ? "+"
                        : ""}
                      {(
                        ((formData.newSalary - selectedEmployee.netSalary) /
                          selectedEmployee.netSalary) *
                        100
                      ).toFixed(1)}
                      %)
                    </span>
                  </div>
                )}
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <TrendingUp size={14} />
                  {t.adjustmentType} *
                </label>
                <ReusableDropdown
                  items={[
                    { id: '', name: t.selectType },
                    { id: 'annual_increase', name: t.annualIncrease },
                    { id: 'promotion', name: t.promotion },
                    { id: 'market_adjustment', name: t.marketAdjustment },
                    { id: 'performance_bonus', name: t.performanceBased },
                    { id: 'cost_of_living', name: t.costOfLiving },
                    { id: 'correction', name: t.salaryCorrection }
                  ]}
                  value={formData.salaryAdjustmentType}
                  onChange={(value) => handleDropdownChange("salaryAdjustmentType", value)}
                  placeholder={t.selectType}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("salaryAdjustmentType", "")}
                />
              </div>
            </>
          );

        case "terminate":
          return (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <AlertCircle size={14} />
                  {t.terminationType} *
                </label>
                <ReusableDropdown
                  items={[
                    { id: '', name: t.selectType },
                    { id: 'voluntary', name: t.voluntaryResignation },
                    { id: 'involuntary', name: t.involuntaryTermination },
                    { id: 'retirement', name: t.retirement },
                    { id: 'contract_end', name: t.contractEnd },
                    { id: 'mutual_agreement', name: t.mutualAgreement },
                    { id: 'layoff', name: t.layoff }
                  ]}
                  value={formData.terminationType}
                  onChange={(value) => handleDropdownChange("terminationType", value)}
                  placeholder={t.selectType}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("terminationType", "")}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Calendar size={14} />
                  {t.lastWorkingDay} *
                </label>
                <input
                  type="date"
                  name="lastWorkingDay"
                  value={formData.lastWorkingDay}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  required
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <FileText size={14} />
                  {t.terminationReason} *
                </label>
                <textarea
                  name="terminationReason"
                  value={formData.terminationReason}
                  onChange={handleInputChange}
                  style={styles.textarea}
                  className="enhanced-input-focus"
                  placeholder={t.provideDetailedTermination}
                  required
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <DollarSign size={14} />
                  {t.severancePackage}
                </label>
                <input
                  type="number"
                  name="severanceAmount"
                  value={formData.severanceAmount}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterSeveranceAmount}
                  min="0"
                  step="100"
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Shield size={14} />
                  {t.eligibleForRehire}
                </label>
                <ReusableDropdown
                  items={[
                    { id: 'yes', name: t.yes },
                    { id: 'no', name: t.no },
                    { id: 'conditional', name: t.conditional }
                  ]}
                  value={formData.eligibleForRehire}
                  onChange={(value) => handleDropdownChange("eligibleForRehire", value)}
                  placeholder={t.selectType}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("eligibleForRehire", "")}
                />
              </div>
            </>
          );

        case "suspend":
          return (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Clock size={14} />
                  {t.suspensionStartDate} *
                </label>
                <input
                  type="date"
                  name="suspensionStartDate"
                  value={formData.suspensionStartDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  required
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Clock size={14} />
                  {t.suspensionEndDate} *
                </label>
                <input
                  type="date"
                  name="suspensionEndDate"
                  value={formData.suspensionEndDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  required
                  min={
                    formData.suspensionStartDate ||
                    new Date().toISOString().split("T")[0]
                  }
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <AlertCircle size={14} />
                  {t.suspensionType} *
                </label>
                <ReusableDropdown
                  items={[
                    { id: '', name: t.selectType },
                    { id: 'with_pay', name: t.withPay },
                    { id: 'without_pay', name: t.withoutPay },
                    { id: 'partial_pay', name: t.partialPay }
                  ]}
                  value={formData.suspensionType}
                  onChange={(value) => handleDropdownChange("suspensionType", value)}
                  placeholder={t.selectType}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("suspensionType", "")}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <FileText size={14} />
                  {t.suspensionReason} *
                </label>
                <textarea
                  name="suspensionReason"
                  value={formData.suspensionReason}
                  onChange={handleInputChange}
                  style={styles.textarea}
                  className="enhanced-input-focus"
                  placeholder={t.provideDetailedSuspension}
                  required
                />
              </div>
            </>
          );

        case "rehire":
          return (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Briefcase size={14} />
                  {t.position} *
                </label>
                <input
                  type="text"
                  name="newPosition"
                  value={formData.newPosition}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterPositionTitle}
                  required
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Building size={14} />
                  {t.department} *
                </label>
                <ReusableDropdown
                  items={departments.map((dept) => ({ id: dept, name: dept }))}
                  value={formData.newDepartment}
                  onChange={(value) => handleDropdownChange("newDepartment", value)}
                  placeholder={t.selectDepartment}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("newDepartment", "")}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <DollarSign size={14} />
                  {t.startingSalary} *
                </label>
                <input
                  type="number"
                  name="newSalary"
                  value={formData.newSalary}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterStartingSalary}
                  required
                  min="0"
                  step="100"
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Calendar size={14} />
                  {t.rehireDate} *
                </label>
                <input
                  type="date"
                  name="rehireDate"
                  value={formData.rehireDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  required
                />
              </div>
            </>
          );

        case "contract":
          return (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <FileCheck size={14} />
                  {t.contractType} *
                </label>
                <ReusableDropdown
                  items={[
                    { id: '', name: t.selectContractType },
                    { id: 'permanent', name: t.permanent },
                    { id: 'fixed_term', name: t.fixedTerm },
                    { id: 'temporary', name: t.temporary },
                    { id: 'part_time', name: t.partTime },
                    { id: 'consultant', name: t.consultant },
                    { id: 'contractor', name: t.contractor }
                  ]}
                  value={formData.contractType}
                  onChange={(value) => handleDropdownChange("contractType", value)}
                  placeholder={t.selectContractType}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("contractType", "")}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Calendar size={14} />
                  {t.contractStartDate} *
                </label>
                <input
                  type="date"
                  name="contractStartDate"
                  value={formData.contractStartDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  required
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Calendar size={14} />
                  {t.contractEndDate}
                </label>
                <input
                  type="date"
                  name="contractEndDate"
                  value={formData.contractEndDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  min={formData.contractStartDate}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <FileText size={14} />
                  {t.contractTerms}
                </label>
                <textarea
                  name="contractTerms"
                  value={formData.contractTerms}
                  onChange={handleInputChange}
                  style={styles.textarea}
                  className="enhanced-input-focus"
                  placeholder={t.enterContractTerms}
                />
              </div>
            </>
          );

        case "rotation":
          return (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Building size={14} />
                  {t.rotationDepartment} *
                </label>
                <ReusableDropdown
                  items={[
                    { id: '', name: t.selectDepartment },
                    ...departments.map(dept => ({ id: dept, name: dept }))
                  ]}
                  value={formData.rotationDepartment}
                  onChange={(value) => handleDropdownChange("rotationDepartment", value)}
                  placeholder={t.selectDepartment}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("rotationDepartment", "")}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Briefcase size={14} />
                  {t.rotationRole} *
                </label>
                <input
                  type="text"
                  name="rotationRole"
                  value={formData.rotationRole}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterRotationRole}
                  required
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Clock size={14} />
                  {t.durationMonths} *
                </label>
                <input
                  type="number"
                  name="rotationDuration"
                  value={formData.rotationDuration}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterDuration}
                  required
                  min="1"
                  max="24"
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Target size={14} />
                  {t.rotationObjectives}
                </label>
                <textarea
                  name="rotationObjectives"
                  value={formData.rotationObjectives}
                  onChange={handleInputChange}
                  style={styles.textarea}
                  className="enhanced-input-focus"
                  placeholder={t.enterObjectives}
                />
              </div>
            </>
          );

        case "review":
          return (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Star size={14} />
                  {t.performanceRating} *
                </label>
                <ReusableDropdown
                  items={[
                    { id: '', name: t.selectRating },
                    { id: 'Excellent', name: t.excellent },
                    { id: 'Very Good', name: t.veryGood },
                    { id: 'Good', name: t.good },
                    { id: 'Average', name: t.average },
                    { id: 'Below Average', name: t.belowAverage },
                    { id: 'Poor', name: t.poor }
                  ]}
                  value={formData.performanceRating}
                  onChange={(value) => handleDropdownChange("performanceRating", value)}
                  placeholder={t.selectRating}
                  getItemLabel={(item) => item.name}
                  getItemKey={(item) => item.id}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => handleDropdownChange("performanceRating", "")}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <BarChart3 size={14} />
                  {t.reviewScore} *
                </label>
                <input
                  type="number"
                  name="reviewScore"
                  value={formData.reviewScore}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterScore}
                  required
                  min="1"
                  max="10"
                  step="0.1"
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <TrendingUp size={14} />
                  {t.strengths}
                </label>
                <textarea
                  name="strengths"
                  value={formData.strengths}
                  onChange={handleInputChange}
                  style={styles.textarea}
                  className="enhanced-input-focus"
                  placeholder={t.listStrengths}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Target size={14} />
                  {t.areasForImprovement}
                </label>
                <textarea
                  name="improvements"
                  value={formData.improvements}
                  onChange={handleInputChange}
                  style={styles.textarea}
                  className="enhanced-input-focus"
                  placeholder={t.listImprovements}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Calendar size={14} />
                  {t.nextReviewDate}
                </label>
                <input
                  type="date"
                  name="nextReviewDate"
                  value={formData.nextReviewDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                />
              </div>
            </>
          );

        case "training":
          return (
            <>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <GraduationCap size={14} />
                  {t.trainingProgram} *
                </label>
                <input
                  type="text"
                  name="trainingProgram"
                  value={formData.trainingProgram}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterTrainingProgram}
                  required
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Calendar size={14} />
                  {t.trainingStartDate} *
                </label>
                <input
                  type="date"
                  name="trainingStartDate"
                  value={formData.trainingStartDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  required
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Calendar size={14} />
                  {t.trainingEndDate} *
                </label>
                <input
                  type="date"
                  name="trainingEndDate"
                  value={formData.trainingEndDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  required
                  min={formData.trainingStartDate}
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <DollarSign size={14} />
                  {t.trainingCost}
                </label>
                <input
                  type="number"
                  name="trainingCost"
                  value={formData.trainingCost}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="enhanced-input-focus"
                  placeholder={t.enterTrainingCost}
                  min="0"
                  step="100"
                />
              </div>

              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Target size={14} />
                  {t.trainingObjectives}
                </label>
                <textarea
                  name="trainingObjectives"
                  value={formData.trainingObjectives}
                  onChange={handleInputChange}
                  style={styles.textarea}
                  className="enhanced-input-focus"
                  placeholder={t.enterTrainingObjectives}
                />
              </div>
            </>
          );

        default:
          return null;
      }
    };

    return (
      <div style={styles.formGrid}>
        {renderActionSpecificFields()}

        {/* Common fields */}
        <div style={styles.controlGroup}>
          <label style={styles.label}>
            <Calendar size={14} />
            {t.effectiveDate} *
          </label>
          <input
            type="date"
            name="effectiveDate"
            value={formData.effectiveDate}
            onChange={handleInputChange}
            style={styles.input}
            className="enhanced-input-focus"
            required
            min={new Date().toISOString().split("T")[0]}
          />
        </div>

        <div style={styles.controlGroup}>
          <label style={styles.label}>
            <FileText size={14} />
            {t.reasonForAction} *
          </label>
          <textarea
            name="reason"
            value={formData.reason}
            onChange={handleInputChange}
            style={styles.textarea}
            className="enhanced-input-focus"
            placeholder={t.provideDetailedReason}
            required
          />
        </div>

        <div style={styles.controlGroup}>
          <label style={styles.label}>
            <FileText size={14} />
            {t.approverNotes} *
          </label>
          <textarea
            name="approverNotes"
            value={formData.approverNotes}
            onChange={handleInputChange}
            style={styles.textarea}
            className="enhanced-input-focus"
            placeholder={t.addAdditionalNotes}
            required
          />
        </div>

        {actionConfig.requiresApproval && (
          <div style={styles.controlGroup}>
            <label style={styles.label}>
              <Shield size={14} />
              {t.approvalRequired}
            </label>
            <div
              style={{
                background:
                  theme === "dark"
                    ? "rgba(59, 130, 246, 0.1)"
                    : "rgba(59, 130, 246, 0.05)",
                padding: "clamp(14px, 2.2vw, 16px)",
                borderRadius: "clamp(10px, 1.5vw, 12px)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                fontSize: "clamp(12px, 1.7vw, 14px)",
                color: theme === "dark" ? "rgba(255,255,255,0.8)" : "#374151",
                fontWeight: "600",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <ShieldCheck size={16} color="#2e7cba" />
                <span style={{ fontWeight: "700", color: "#2e7cba" }}>
                  {t.approvalRequired}
                </span>
              </div>
              {t.thisActionRequires}{" "}
              <strong>{t[actionConfig.approvalLevelKey]}</strong>
            </div>
          </div>
        )}
      </div>
    );
  }, [
    selectedAction,
    formData,
    handleInputChange,
    styles,
    theme,
    selectedEmployee,
    departments,
    t,
    isRTL,
    language,
  ]);

  // MAIN RENDER
  return (
    <div>
      {/* Header Section */}
      <div style={styles.headerCard}>
        <div style={styles.headerGradientOverlay}></div>
        <div style={{ position: "relative", zIndex: 1 }}>
          <h1 style={styles.welcomeTitle}>
            <Users size={window.innerWidth < 768 ? 32 : 38} />
            {t.title}
          </h1>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div
            style={{
              ...styles.statIconWrapper,
              background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
            }}
          >
            <Users size={28} color="white" />
          </div>
          <div style={styles.statValue}>{stats.totalEmployees}</div>
          <div style={styles.statLabel}>{t.totalEmployees}</div>
          <div style={{ ...styles.statTrend, color: "#10b981" }}>
            <TrendingUp size={15} />
            <span>{t.activeWorkforce}</span>
          </div>
        </div>

        <div style={styles.statCard}>
          <div
            style={{
              ...styles.statIconWrapper,
              background: "linear-gradient(135deg, #10b981, #059669)",
            }}
          >
            <TrendingUp size={28} color="white" />
          </div>
          <div style={styles.statValue}>{stats.promotionsThisMonth}</div>
          <div style={styles.statLabel}>{t.promotionsThisMonth}</div>
          <div style={{ ...styles.statTrend, color: "#10b981" }}>
            <ArrowUpRight size={15} />
            <span>{t.careerGrowth}</span>
          </div>
        </div>

        <div style={styles.statCard}>
          <div
            style={{
              ...styles.statIconWrapper,
              background: "linear-gradient(135deg, #2e7cba, #7c3aed)",
            }}
          >
            <MapPin size={28} color="white" />
          </div>
          <div style={styles.statValue}>{stats.transfersThisMonth}</div>
          <div style={styles.statLabel}>{t.transfersThisMonth}</div>
          <div style={{ ...styles.statTrend, color: "#2e7cba" }}>
            <Activity size={15} />
            <span>{t.departmentMoves}</span>
          </div>
        </div>

        <div style={styles.statCard}>
          <div
            style={{
              ...styles.statIconWrapper,
              background: "linear-gradient(135deg, #f59e0b, #d97706)",
            }}
          >
            <BarChart3 size={28} color="white" />
          </div>
          <div style={styles.statValue}>{stats.avgSalaryIncrease}%</div>
          <div style={styles.statLabel}>{t.avgSalaryIncrease}</div>
          <div style={{ ...styles.statTrend, color: "#f59e0b" }}>
            <ArrowUpRight size={15} />
            <span>{t.compensationGrowth}</span>
          </div>
        </div>

        <button
          style={{
            ...styles.statCard,
            cursor: "pointer",
            border: "none",
            outline: "none",
          }}
          onClick={handleOpenPendingModal}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleOpenPendingModal();
            }
          }}
          tabIndex={0}
          aria-label={`${t.pendingApprovals}: ${stats.pendingApprovals}`}
        >
          <div
            style={{
              ...styles.statIconWrapper,
              background: "linear-gradient(135deg, #ec4899, #db2777)",
            }}
          >
            <Clock size={28} color="white" />
          </div>
          <div style={styles.statValue}>{stats.pendingApprovals}</div>
          <div style={styles.statLabel}>{t.pendingApprovals}</div>
          <div style={{ ...styles.statTrend, color: "#ec4899" }}>
            <AlertCircle size={15} />
            <span>{t.awaitingReview}</span>
          </div>
        </button>

        <div style={styles.statCard}>
          <div
            style={{
              ...styles.statIconWrapper,
              background: "linear-gradient(135deg, #06b6d4, #0891b2)",
            }}
          >
            <Check size={28} color="white" />
          </div>
          <div style={styles.statValue}>{stats.completedActions}</div>
          <div style={styles.statLabel}>{t.completedActions}</div>
          <div style={{ ...styles.statTrend, color: "#06b6d4" }}>
            <Check size={15} />
            <span>{t.thisMonth}</span>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMsg && (
        <div
          style={{ ...styles.alertBox, ...styles.successAlert }}
          className="fade-in-alert"
        >
          <Check size={22} />
          <span style={{ fontWeight: "700" }}>{successMsg}</span>
        </div>
      )}

      {/* Error Message */}
      {errorMsg && (
        <div
          style={{ ...styles.alertBox, ...styles.errorAlert }}
          className="fade-in-alert"
        >
          <AlertCircle size={22} />
          <span style={{ fontWeight: "700" }}>{errorMsg}</span>
        </div>
      )}

      {/* Filter Section */}
      <div style={styles.filterCard}>
        <div
          style={{
            display: "flex",
            flexDirection: isRTL ? "row" : "row",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "clamp(22px, 3.5vw, 28px)",
            paddingBottom: "clamp(18px, 2.8vw, 22px)",
            borderBottom:
              theme === "dark"
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.1)",
            flexWrap: "wrap",
            gap: "18px",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "clamp(18px, 3.2vw, 22px)",
                fontWeight: "900",
                color: theme === "dark" ? "#ffffff" : "#0f172a",
                margin: "0 0 8px 0",
                letterSpacing: "-0.02em",
                display: "flex",
                flexDirection: isRTL ? "row" : "row",
                alignItems: "center",
                gap: "clamp(10px, 1.5vw, 12px)",
              }}
            >
              <Filter size={window.innerWidth < 768 ? 20 : 22} />
              {t.searchFilterEmployees}
            </h3>
            <p
              style={{
                fontSize: "clamp(12px, 1.7vw, 14px)",
                color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                margin: 0,
                fontWeight: "600",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {t.showing} {filteredEmployees.length} {t.of} {employees.length}{" "}
              {t.employees}
              {(userRole === "Admin" ||
                userRole === "admin") &&
                selectedClient !== "all" &&
                ` ${t.forSelectedClient}`}
              {" "}
              {userRole !== "Admin" &&
                userRole !== "admin" &&
                ``}
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: isRTL ? "row" : "row",
              gap: "14px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              style={styles.secondaryButton}
              onClick={() => setShowHistoryModal(true)}
            // className="enhanced-button-hover"
            >
              <History size={18} /> {t.viewHistory}
            </button>
            {/* <button
              style={styles.primaryButton}
              onClick={() => setRefreshTrigger((prev) => prev + 1)}
              className="enhanced-button-hover"
            >
              <RefreshCw size={18} /> {t.refresh}
            </button> */}
          </div>
        </div>

        <div style={styles.controlsGrid}>
          <div style={styles.controlGroup}>
            <label style={styles.label}>
              <Search size={15} />
              {t.searchEmployee}
            </label>
            <div style={{ position: "relative" }}>
              <div style={{
                position: 'absolute',
                [isRTL ? 'right' : 'left']: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : '#94a3b8',
                zIndex: 1,
              }}>
                <Search size={18} />
              </div>

              <ReusableDropdown
                items={employees}
                value={searchTerm}
                onChange={setSearchTerm}
                onSelect={(emp) => {
                  // When selecting, just let onChange handle it via the itemId
                  // No need to set anything extra here
                }}
                getItemKey={(emp) => emp.infinityCode}
                getItemLabel={getEmployeeName}
                placeholder={t.searchPlaceholder}
                theme={theme}
                language={language}
                maxItems={8}
                filterFunction={(emp, search) => {
                  const s = search.toLowerCase();
                  return getEmployeeName(emp).toLowerCase().includes(s) ||
                    (emp.email || '').toLowerCase().includes(s) ||
                    (emp.infinityCode || '').toString().includes(s) ||
                    (emp.employeeType || '').toLowerCase().includes(s);
                }}
                inputStyle={{
                  paddingLeft: isRTL ? '16px' : '52px',
                  paddingRight: isRTL ? '52px' : '16px',
                }}
                onClear={() => {
                  setSearchTerm("");
                }}
              />
            </div>
          </div>

          {/* إظهار فلتر Client للـ Admin فقط */}
          {(userRole === "Admin" ||
            userRole === "admin") && (
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Building size={15} />
                  {t.client}
                </label>
                <div style={{ position: "relative" }}>

                  <ReusableDropdown
                    items={[
                      { id: 'all', name: t.allClients || 'All' },
                      ...clients.map(client => ({
                        id: client.id,
                        name: client.clientName
                      }))
                    ]}
                    value={selectedClient}
                    onChange={(val) => setSelectedClient(val || 'all')}
                    onSelect={(item) => {
                      const id = typeof item === "string"
                        ? item
                        : item?.id || String(item);
                      setSelectedClient(id);
                    }}
                    getItemKey={(it) =>
                      typeof it === "string" ? it : it.id || String(it)
                    }
                    getItemLabel={(it) =>
                      typeof it === "string" ? it : it.name || String(it)
                    }
                    placeholder={t.selectClient || "اختر عميل..."}
                    theme={theme}
                    language={language}
                    maxItems={10}
                    onClear={() => setSelectedClient("all")}
                  />
                </div>
              </div>
            )}

          <div style={styles.controlGroup}>
            <label style={styles.label}>
              <Building size={15} />
              {t.department}
            </label>
            <div style={{ position: "relative" }}>
              <ReusableDropdown
                items={[
                  { id: 'all', name: t.allDepartments || 'All Departments' },
                  ...departments.map(dept => ({
                    id: dept,
                    name: dept
                  }))
                ]}
                value={filterDepartment}
                onChange={(val) => setFilterDepartment(val || 'all')}
                onSelect={(item) => {
                  const value = typeof item === "string"
                    ? item
                    : item?.id || String(item);
                  setFilterDepartment(value);
                }}
                getItemKey={(it) =>
                  typeof it === "string" ? it : it.id || String(it)
                }
                getItemLabel={(it) =>
                  typeof it === "string" ? it : it.name || String(it)
                }
                placeholder={t.selectDepartment || "اختر قسم..."}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => setFilterDepartment("all")}
              />
            </div>
          </div>

          {/* <div style={styles.controlGroup}>
            <label style={styles.label}>
              <Star size={15} />
              {t.performance}
            </label>
            <div style={{ position: "relative" }}>
              <ReusableDropdown
                items={[
                  { id: 'all', name: t.allPerformanceLevels || 'All' },
                  ...performances.map(perf => ({
                    id: perf,
                    name: perf
                  }))
                ]}
                value={filterPerformance}
                onChange={(val) => setFilterPerformance(val || 'all')}
                onSelect={(item) => {
                  const value = typeof item === "string"
                    ? item
                    : item?.id || String(item);
                  setFilterPerformance(value);
                }}
                getItemKey={(it) =>
                  typeof it === "string" ? it : it.id || String(it)
                }
                getItemLabel={(it) =>
                  typeof it === "string" ? it : it.name || String(it)
                }
                placeholder={t.selectPerformance || "اختر الأداء..."}
                theme={theme}
                language={language}
                maxItems={10}
              />
            </div>
          </div> */}


          <div style={styles.controlGroup}>
            <label style={styles.label}>
              <UserCheck size={15} />
              {t.status}
            </label>
            <div style={{ position: "relative" }}>
              <ReusableDropdown
                items={[
                  { id: 'all', name: t.allStatuses || 'All' },
                  ...statuses.map(status => ({
                    id: status,
                    name: status
                  }))
                ]}
                value={filterStatus}
                onChange={(val) => setFilterStatus(val || 'all')}
                onSelect={(item) => {
                  const value = typeof item === "string"
                    ? item
                    : item?.id || String(item);
                  setFilterStatus(value);
                }}
                getItemKey={(it) =>
                  typeof it === "string" ? it : it.id || String(it)
                }
                getItemLabel={(it) =>
                  typeof it === "string" ? it : it.name || String(it)
                }
                placeholder={t.selectStatus || "اختر الحالة..."}
                theme={theme}
                language={language}
                maxItems={10}
                onClear={() => setFilterStatus("all")}
              />
            </div>
          </div>


          <div style={styles.controlGroup}>
            <label style={styles.label}>
              <BarChart3 size={15} />
              {t.sortBy}
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <ReusableDropdown
                  items={[
                    { id: 'name', name: t.name },
                    { id: 'salary', name: t.salary },
                    { id: 'joinDate', name: t.joinDate },
                    { id: 'performance', name: t.performance },
                  ]}
                  value={sortBy}
                  onChange={(val) => setSortBy(val || 'name')}
                  onSelect={(item) => setSortBy(item.id)}
                  getItemKey={(it) => (typeof it === 'string' ? it : it.id || String(it))}
                  getItemLabel={(it) => (typeof it === 'string' ? it : it.name || String(it))}
                  placeholder={t.sortBy}
                  theme={theme}
                  language={language}
                  maxItems={10}
                  onClear={() => setSortBy("name")}
                />
              </div>

              <button
                style={{
                  ...styles.select,
                  //minWidth: '10%',
                  // padding: '6px',
                  fontSize: 'clamp(20px, 3vw, 24px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  //transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  cursor: 'pointer',
                }}
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                // className="enhanced-button-hover"
                title={sortOrder === 'asc' ? t.ascendingOrder : t.descendingOrder}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Employee Table */}
      <div style={styles.tableCard}>
        {isLoading && (
          <div style={styles.loadingOverlay}>
            <div style={{ textAlign: "center" }}>
              <Loader size={48} style={styles.loadingSpinner} color="#2e7cba" />
              <p
                style={{
                  marginTop: "16px",
                  fontWeight: "600",
                  color: theme === "dark" ? "rgba(255,255,255,0.8)" : "#64748b",
                }}
              >
                {t.loadingEmployees}
              </p>
            </div>
          </div>
        )}

        <div style={styles.tableWrapper}>
          {filteredEmployees.length > 0 ? (
            <>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{t.employee}</th>
                    <th style={styles.th}>{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((emp) => {
                    // const isSelected = selectedEmployees.has(emp.id);

                    return (
                      <tr>
                        <td style={styles.td}>
                          <div style={styles.employeeCell}>
                            <div
                              className="employeeAvatar"
                              style={{
                                ...styles.employeeAvatar,
                                position: "relative",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              {employeeImages[emp.infinityCode] && (
                                <img
                                  src={employeeImages[emp.infinityCode]}
                                  alt={getEmployeeName(emp) || ""}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 2,
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              )}
                              <div
                                className="avatar-initials"
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  zIndex: 1,
                                }}
                              >
                                {getInitials(getEmployeeName(emp), emp.sr)}
                              </div>
                            </div>
                            <div style={styles.employeeInfo}>
                              <div style={styles.employeeName}>
                                {getEmployeeName(emp)}
                              </div>
                              <div style={styles.employeeDetail}>
                                <Mail size={13} />
                                {emp.email || t.notAvailable}
                              </div>
                              <div style={styles.employeeDetail}>
                                <Phone size={13} />
                                {emp.phoneNumber || t.notAvailable}
                              </div>
                              {emp.infinityCode && (
                                <div style={styles.employeeDetail}>
                                  <Code size={13} />
                                  {emp.infinityCode}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>


























                        <td style={styles.td}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: isRTL ? "row" : "row",
                              gap: "8px",
                              flexWrap: "nowrap",
                              alignItems: "center",
                              position: "relative",
                            }}
                          >
                            <button
                              style={{
                                ...styles.actionButton,
                                background:
                                  "linear-gradient(135deg, #2e7cba, #2e7cba)",
                                minWidth: "fit-content",
                              }}
                              onClick={() => openActionModal(emp)}
                              onMouseEnter={() => setHoveredActionBtn(emp.infinityCode)}
                              onMouseLeave={() => setHoveredActionBtn(null)}
                            >
                              <Zap size={16} />
                            </button>
                            {hoveredActionBtn === emp.infinityCode && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  marginTop: "4px",
                                  background: "rgba(0,0,0,0.75)",
                                  color: "#fff",
                                  fontSize: "11px",
                                  whiteSpace: "nowrap",
                                  padding: "3px 8px",
                                  borderRadius: "4px",
                                  pointerEvents: "none",
                                  zIndex: 999,
                                }}
                              >
                                {t.employeeAction || "Employee Action"}
                              </span>
                            )}

                            <button
                              style={{
                                ...styles.actionButton,
                                background:
                                  "linear-gradient(135deg, #06b6d4, #0891b2)",
                                minWidth: "fit-content",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEmployeeHistory(emp);
                              }}
                              onMouseEnter={() => setHoveredActionBtn(`history_${emp.infinityCode}`)}
                              onMouseLeave={() => setHoveredActionBtn(null)}
                            >
                              <History size={16} />
                            </button>
                            {hoveredActionBtn === `history_${emp.infinityCode}` && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  marginTop: "4px",
                                  background: "rgba(0,0,0,0.75)",
                                  color: "#fff",
                                  fontSize: "11px",
                                  whiteSpace: "nowrap",
                                  padding: "3px 8px",
                                  borderRadius: "4px",
                                  pointerEvents: "none",
                                  zIndex: 999,
                                }}
                              >
                                {t.employeeHistory || "Employee History"}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* pagination removed; show full results */}
            </>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>
                <Users size={100} />
              </div>
              <h3 style={styles.emptyStateTitle}>
                {isLoading ? t.loadingEmployees : t.noEmployeesFound}
              </h3>
              <p style={styles.emptyStateText}>
                {isLoading ? t.pleaseWait : t.noEmployeesMatch}
              </p>
              {!isLoading && (
                <button
                  style={styles.primaryButton}
                  onClick={() => {
                    setSearchTerm("");
                    setFilterDepartment("all");
                    setFilterPerformance("all");
                    setFilterStatus("all");
                    setSelectedClient("all");
                  }}
                  className="enhanced-button-hover"
                >
                  <RefreshCw size={18} /> {t.clearFilters}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div
          style={styles.modal}
          onClick={() => {
            setShowActionModal(false);
            setIsDropdownOpen(false);
          }}
        >
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    ...styles.modalIconWrapper,
                    background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
                  }}
                >
                  <Zap size={26} color="white" />
                </div>
                <h2 style={styles.modalTitle}>{t.employeeAction}</h2>
              </div>
              {/* <button
                style={styles.closeButton}
                onClick={() => {
                  setShowActionModal(false);
                  setIsDropdownOpen(false);
                }}
                className="enhanced-button-hover"
              >
                <X size={24} />
              </button> */}
            </div>

            {selectedEmployee && (
              <div style={styles.infoBox}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>{t.employeeName}</span>
                  <span style={styles.infoValue}>
                    {getEmployeeName(selectedEmployee)}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>{t.infinityCode}</span>
                  <span style={styles.infoValue}>
                    {selectedEmployee.infinityCode || t.notAvailable}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>{t.currentPosition}</span>
                  <span style={styles.infoValue}>
                    {selectedEmployee.employeeType || t.notAvailable}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>{t.department}</span>
                  <span style={styles.infoValue}>
                    {selectedEmployee.employeeType || t.notAvailable}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>{t.currentSalary}</span>
                  <span style={styles.infoValue}>
                    {selectedEmployee.netSalary
                      ? `${t.egp
                      } ${selectedEmployee.netSalary.toLocaleString()}`
                      : t.notAvailable}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>{t.performanceRating}</span>
                  <span style={styles.infoValue}>
                    {selectedEmployee.performance || t.notAvailable}
                  </span>
                </div>
                <div
                  style={{
                    ...styles.infoRow,
                    borderBottom: "none",
                    marginBottom: 0,
                    paddingBottom: 0,
                  }}
                >
                  <span style={styles.infoLabel}>{t.reportsTo}</span>
                  <span style={styles.infoValue}>
                    {selectedEmployee.firstLineManagerName || t.notAvailable}
                  </span>
                </div>
              </div>
            )}

            {errorMsg && (
              <div
                style={{ ...styles.alertBox, ...styles.errorAlert }}
                className="fade-in-alert"
              >
                <AlertCircle size={20} />
                <span>{errorMsg}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={styles.controlGroup}>
                <label style={styles.label}>
                  <Sparkles size={15} />
                  {t.actionType} *
                </label>
                {CustomActionDropdown}
              </div>

              {renderFormFields()}

              <div
                style={{
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  gap: "14px",
                  marginTop: "clamp(28px, 4.5vw, 36px)",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  style={{ ...styles.secondaryButton, flex: 1 }}
                  onClick={() => {
                    setShowActionModal(false);
                    setIsDropdownOpen(false);
                  }}
                // className="enhanced-button-hover"
                >
                  <X size={18} /> {t.cancel}
                </button>
                {(() => {
                  const featureKey = ACTION_FEATURE_MAP[selectedAction] || null;
                  const isActionAllowedForAdmin =
                    !isAdminView ||
                    isAdminRole ||
                    (Array.isArray(permissions?.features?.changesPage) && featureKey && permissions.features.changesPage.includes(featureKey));

                  if (!isActionAllowedForAdmin) return null;

                  return (
                    <button
                      type="submit"
                      style={{ ...styles.primaryButton, flex: 1 }}
                      disabled={!selectedAction}
                    >
                      <Check size={18} /> {t.submitAction}
                    </button>
                  );
                })()}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && pendingAction && (
        <div style={styles.modal} onClick={() => setShowConfirmModal(false)}>
          <div
            style={{ ...styles.modalContent, maxWidth: "min(90vw, 600px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    ...styles.modalIconWrapper,
                    background: pendingAction.actionConfig.color,
                  }}
                >
                  <AlertCircle size={26} color="white" />
                </div>
                <h2 style={styles.modalTitle}>{t.confirmAction}</h2>
              </div>
              <button
                style={styles.closeButton}
                onClick={() => setShowConfirmModal(false)}
                className="enhanced-button-hover"
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ ...styles.alertBox, ...styles.warningAlert }}>
              <AlertCircle size={22} />
              <span
                style={{
                  fontWeight: "700",
                  fontSize: "clamp(13px, 1.9vw, 15px)",
                }}
              >
                {t.reviewBeforeConfirm}
              </span>
            </div>

            <div style={styles.infoBox}>
              <h3
                style={{
                  fontSize: "clamp(16px, 2.8vw, 18px)",
                  fontWeight: "800",
                  marginBottom: "16px",
                  color: theme === "dark" ? "#ffffff" : "#0f172a",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t.actionDetails}
              </h3>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>{t.actionType}</span>
                <span
                  style={{
                    ...styles.badge,
                    background: pendingAction.actionConfig.bgColor,
                    border: `1px solid ${pendingAction.actionConfig.borderColor}`,
                    color: theme === "dark" ? "#ffffff" : "#0f172a",
                  }}
                >
                  {t[pendingAction.actionConfig.labelKey]}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>{t.employee}</span>
                <span style={styles.infoValue}>
                  {getEmployeeName(pendingAction.employee)}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>{t.infinityCode}</span>
                <span style={styles.infoValue}>
                  {pendingAction.employee.infinityCode}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>{t.effectiveDate}</span>
                <span style={styles.infoValue}>
                  {new Date(
                    pendingAction.formData.effectiveDate
                  ).toLocaleDateString()}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>{t.reason}</span>
                <span
                  style={{
                    ...styles.infoValue,
                    fontSize: "clamp(12px, 1.7vw, 14px)",
                  }}
                >
                  {pendingAction.formData.reason}
                </span>
              </div>
              {pendingAction.actionConfig.requiresApproval && (
                <div
                  style={{
                    ...styles.infoRow,
                    borderBottom: "none",
                    marginBottom: 0,
                    paddingBottom: 0,
                  }}
                >
                  <span style={styles.infoLabel}>{t.approvalRequired}</span>
                  <span
                    style={{
                      ...styles.badge,
                      background: "rgba(245, 158, 11, 0.15)",
                      border: "1px solid rgba(245, 158, 11, 0.4)",
                      color: theme === "dark" ? "#fbbf24" : "#d97706",
                    }}
                  >
                    <Clock size={14} />
                    {t[pendingAction.actionConfig.approvalLevelKey]}
                  </span>
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: isRTL ? "row" : "row",
                gap: "14px",
                marginTop: "clamp(24px, 3.8vw, 32px)",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                style={{ ...styles.secondaryButton, flex: 1 }}
                onClick={() => setShowConfirmModal(false)}
                //className="enhanced-button-hover"
                disabled={isSubmitting}
              >
                <X size={18} /> {t.cancel}
              </button>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  flex: 1,
                  background: pendingAction.actionConfig.color,
                }}
                onClick={handleConfirmAction}
                className="enhanced-button-hover"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader size={18} style={styles.loadingSpinner} />{" "}
                    {t.processing}
                  </>
                ) : (
                  <>
                    <Check size={18} /> {t.confirmSubmit}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div style={styles.modal} onClick={() => setShowHistoryModal(false)}>
          <div
            style={{ ...styles.modalContent, maxWidth: "min(95vw, 1200px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    ...styles.modalIconWrapper,
                    background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                  }}
                >
                  <History size={26} color="white" />
                </div>
                <h2 style={styles.modalTitle}>{t.actionHistory}</h2>
              </div>
              <button
                style={styles.closeButton}
                onClick={() => setShowHistoryModal(false)}
                className="enhanced-button-hover"
              >
                <X size={24} />
              </button>
            </div>

            <div style={styles.tableWrapper}>
              {actionHistory.length > 0 ? (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>{t.date}</th>
                      <th style={styles.th}>{t.employee}</th>
                      <th style={styles.th}>{t.actionType}</th>
                      <th style={styles.th}>{t.status}</th>
                      <th style={styles.th}>{t.details}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actionHistory.map((action) => {
                      const actionConfig = ACTION_TYPES.find(
                        (a) => a.id === action.actionType
                      );
                      const ActionIcon = actionConfig?.icon || FileText;

                      return (
                        <tr key={action.id} className="table-row-hover">
                          <td style={styles.td}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: isRTL ? "row" : "row",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Calendar size={16} />
                              {new Date(
                                action.effectiveDate ||
                                action.createdAt ||
                                action.requestedDate
                              ).toLocaleDateString(
                                language === "ar" ? "ar-EG" : "en-US"
                              )}
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.employeeInfo}>
                              <div style={styles.employeeName}>
                                {language === "ar"
                                  ? action.employeeArabicName ||
                                  action.employeeName ||
                                  t.notAvailable
                                  : action.employeeName ||
                                  action.employeeArabicName ||
                                  t.notAvailable}
                              </div>
                              <div style={styles.employeeDetail}>
                                <Code size={13} />
                                {action.infinityCode || action.employeeId}
                              </div>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div
                              style={{
                                ...styles.badge,
                                background:
                                  actionConfig?.bgColor ||
                                  "rgba(100, 116, 139, 0.15)",
                                border: `1px solid ${actionConfig?.borderColor ||
                                  "rgba(100, 116, 139, 0.4)"
                                  }`,
                                color: theme === "dark" ? "#ffffff" : "#0f172a",
                              }}
                            >
                              <ActionIcon size={14} />
                              {actionConfig
                                ? t[actionConfig.labelKey]
                                : action.actionType}
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div
                              style={{
                                ...styles.badge,
                                background:
                                  action.status === "approved" ||
                                    action.status === "completed"
                                    ? "rgba(16, 185, 129, 0.15)"
                                    : action.status === "pending"
                                      ? "rgba(245, 158, 11, 0.15)"
                                      : "rgba(239, 68, 68, 0.15)",
                                border:
                                  action.status === "approved" ||
                                    action.status === "completed"
                                    ? "1px solid rgba(16, 185, 129, 0.4)"
                                    : action.status === "pending"
                                      ? "1px solid rgba(245, 158, 11, 0.4)"
                                      : "1px solid rgba(239, 68, 68, 0.4)",
                                color:
                                  action.status === "approved" ||
                                    action.status === "completed"
                                    ? theme === "dark"
                                      ? "#34d399"
                                      : "#059669"
                                    : action.status === "pending"
                                      ? theme === "dark"
                                        ? "#fbbf24"
                                        : "#d97706"
                                      : theme === "dark"
                                        ? "#f87171"
                                        : "#dc2626",
                              }}
                            >
                              {action.status === "approved" ||
                                action.status === "completed" ? (
                                <Check size={14} />
                              ) : action.status === "pending" ? (
                                <Clock size={14} />
                              ) : (
                                <X size={14} />
                              )}
                              {t[action.status] || action.status || t.unknown}
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div
                              style={{
                                fontSize: "clamp(11px, 1.6vw, 13px)",
                                color:
                                  theme === "dark"
                                    ? "rgba(255,255,255,0.65)"
                                    : "#64748b",
                                maxWidth: "300px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {action.reason || t.noDetailsProvided}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={styles.emptyState}>
                  <div style={styles.emptyStateIcon}>
                    <History size={80} />
                  </div>
                  <h3 style={styles.emptyStateTitle}>{t.noActionHistory}</h3>
                  <p style={styles.emptyStateText}>{t.noActionsRecorded}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pending Approvals Modal */}
      {showPendingModal && (
        <div style={styles.modal} onClick={() => setShowPendingModal(false)}>
          <div
            style={{ ...styles.modalContent, maxWidth: "min(95vw, 1200px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    ...styles.modalIconWrapper,
                    background: "linear-gradient(135deg, #ec4899, #db2777)",
                  }}
                >
                  <Clock size={26} color="white" />
                </div>
                <div>
                  <h2 style={styles.modalTitle}>{t.pendingApprovalsTitle}</h2>
                  <p
                    style={{
                      fontSize: "clamp(12px, 1.7vw, 14px)",
                      color:
                        theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                      margin: "4px 0 0 0",
                      fontWeight: "600",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {pendingActions.length} {t.actionsAwaiting}
                  </p>
                </div>
              </div>
              <button
                style={styles.closeButton}
                onClick={() => setShowPendingModal(false)}
                className="enhanced-button-hover"
              >
                <X size={24} />
              </button>
            </div>

            {isSubmitting && (
              <div style={styles.loadingOverlay}>
                <div style={{ textAlign: "center" }}>
                  <Loader
                    size={48}
                    style={styles.loadingSpinner}
                    color="#2e7cba"
                  />
                  <p
                    style={{
                      marginTop: "16px",
                      fontWeight: "600",
                      color:
                        theme === "dark" ? "rgba(255,255,255,0.8)" : "#64748b",
                    }}
                  >
                    {t.processing}
                  </p>
                </div>
              </div>
            )}

            <div style={styles.tableWrapper}>
              {pendingActions.length > 0 ? (
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>{t.requestedDate}</th>
                      <th style={styles.th}>{t.employee}</th>
                      <th style={styles.th}>{t.actionType}</th>
                      <th style={styles.th}>{t.effectiveDate}</th>
                      <th style={styles.th}>{t.details}</th>
                      <th style={styles.th}>{t.approvalLevel}</th>
                      <th style={styles.th}>{t.actions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingActions.map((action) => {
                      const actionConfig = ACTION_TYPES.find(
                        (a) => a.id === action.actionType
                      );
                      const ActionIcon = actionConfig?.icon || FileText;

                      return (
                        <tr key={action.id} className="table-row-hover">
                          <td style={styles.td}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: isRTL ? "row" : "row",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Calendar size={16} />
                              {new Date(
                                action.createdAt || action.requestedDate
                              ).toLocaleDateString(
                                language === "ar" ? "ar-EG" : "en-US"
                              )}
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.employeeInfo}>
                              <div style={styles.employeeName}>
                                {language === "ar"
                                  ? action.employeeArabicName ||
                                  action.employeeName ||
                                  action.newData?.employeeName ||
                                  t.notAvailable
                                  : action.employeeName ||
                                  action.employeeArabicName ||
                                  action.newData?.employeeName ||
                                  t.notAvailable}
                              </div>
                              <div style={styles.employeeDetail}>
                                <Code size={13} />
                                {action.infinityCode ||
                                  action.employeeId ||
                                  t.notAvailable}
                              </div>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div
                              style={{
                                ...styles.badge,
                                background:
                                  actionConfig?.bgColor ||
                                  "rgba(100, 116, 139, 0.15)",
                                border: `1px solid ${actionConfig?.borderColor ||
                                  "rgba(100, 116, 139, 0.4)"
                                  }`,
                                color: theme === "dark" ? "#ffffff" : "#0f172a",
                              }}
                            >
                              <ActionIcon size={14} />
                              {actionConfig
                                ? t[actionConfig.labelKey]
                                : action.actionType}
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: isRTL ? "row" : "row",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Calendar size={16} />
                              {action.effectiveDate
                                ? new Date(
                                  action.effectiveDate
                                ).toLocaleDateString(
                                  language === "ar" ? "ar-EG" : "en-US"
                                )
                                : t.notAvailable}
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div
                              style={{
                                fontSize: "clamp(11px, 1.6vw, 13px)",
                                color:
                                  theme === "dark"
                                    ? "rgba(255,255,255,0.65)"
                                    : "#64748b",
                                maxWidth: "250px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: isRTL ? "right" : "left",
                              }}
                              title={action.reason}
                            >
                              {action.reason || t.noDetailsProvided}
                            </div>
                            {action.oldData && action.newData && (
                              <div
                                style={{
                                  fontSize: "clamp(10px, 1.5vw, 11px)",
                                  color:
                                    theme === "dark"
                                      ? "rgba(255,255,255,0.5)"
                                      : "#94a3b8",
                                  marginTop: "4px",
                                  textAlign: isRTL ? "right" : "left",
                                }}
                              >
                                {action.oldData.position &&
                                  action.newData.position &&
                                  `${action.oldData.position} ${isRTL ? "←" : "→"
                                  } ${action.newData.position}`}
                                {action.oldData.salary &&
                                  action.newData.salary &&
                                  ` | ${t.egp
                                  } ${action.oldData.salary?.toLocaleString()} ${isRTL ? "←" : "→"
                                  } ${action.newData.salary?.toLocaleString()}`}
                              </div>
                            )}
                          </td>
                          <td style={styles.td}>
                            <div
                              style={{
                                ...styles.badge,
                                background: "rgba(139, 92, 246, 0.15)",
                                border: "1px solid rgba(139, 92, 246, 0.4)",
                                color: theme === "dark" ? "#c4b5fd" : "#7c3aed",
                              }}
                            >
                              <Shield size={14} />
                              {actionConfig
                                ? t[actionConfig.approvalLevelKey]
                                : t.notAvailable}
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: isRTL ? "row" : "row",
                                gap: "8px",
                                flexWrap: "wrap",
                              }}
                            >
                              {
                                (() => {
                                  const currentUser = profile?.infinityCode || localStorage.getItem("infinityCode");
                                  const wf = pendingWorkflows?.[action.id] || [];
                                  const isApprover = Array.isArray(wf)
                                    ? wf.some(
                                      (step) =>
                                        step.status === "pending" &&
                                        (String(step.assignedTo) === String(currentUser) ||
                                          String(step.assignedTo) === String(parseInt(currentUser, 10)))
                                    )
                                    : false;

                                  if (!isApprover) {
                                    return null; // show no action buttons if user is not in workflow
                                  }

                                  // Check permissions for approve/reject in Admin view
                                  const canApprove = !isAdminView || isAdminRole || (Array.isArray(permissions?.features?.changesPage) && permissions.features.changesPage.includes("approveActions"));
                                  const canReject = !isAdminView || isAdminRole || (Array.isArray(permissions?.features?.changesPage) && permissions.features.changesPage.includes("rejectActions"));

                                  return (
                                    <>
                                      {canApprove && (
                                        <button
                                          style={{
                                            ...styles.actionButton,
                                            background:
                                              "linear-gradient(135deg, #10b981, #059669)",
                                            padding:
                                              "clamp(8px, 1.3vw, 10px) clamp(14px, 2vw, 16px)",
                                            fontSize: "clamp(11px, 1.6vw, 12px)",
                                          }}
                                          onClick={() => handleApproveAction(action.id)}
                                          className="action-btn-hover"
                                          disabled={isSubmitting}
                                        >
                                          <Check size={14} /> {t.approve}
                                        </button>
                                      )}

                                      {canReject && (
                                        <button
                                          style={{
                                            ...styles.actionButton,
                                            background:
                                              "linear-gradient(135deg, #ef4444, #dc2626)",
                                            padding:
                                              "clamp(8px, 1.3vw, 10px) clamp(14px, 2vw, 16px)",
                                            fontSize: "clamp(11px, 1.6vw, 12px)",
                                          }}
                                          onClick={() => {
                                            setRejectingActionId(action.id);
                                            setRejectingActionData(action);
                                            setRejectionReason("");
                                            setShowRejectModal(true);
                                          }}
                                          className="action-btn-hover"
                                          disabled={isSubmitting}
                                        >
                                          <X size={14} /> {t.reject}
                                        </button>
                                      )}
                                    </>
                                  );
                                })()
                              }
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={styles.emptyState}>
                  <div style={styles.emptyStateIcon}>
                    <Clock size={80} />
                  </div>
                  <h3 style={styles.emptyStateTitle}>{t.noPendingApprovals}</h3>
                  <p style={styles.emptyStateText}>{t.allActionsReviewed}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Employee History Timeline Modal */}
      {showEmployeeHistoryModal && selectedEmployeeHistory && (
        <div
          style={styles.modal}
          onClick={() => setShowEmployeeHistoryModal(false)}
        >
          <div
            style={{ ...styles.modalContent, maxWidth: "min(95vw, 900px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    ...styles.modalIconWrapper,
                    background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                  }}
                >
                  <History size={26} color="white" />
                </div>
                <div>
                  <h2 style={styles.modalTitle}>{t.employeeHistoryTitle}</h2>
                  <p
                    style={{
                      fontSize: "clamp(12px, 1.7vw, 14px)",
                      color:
                        theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                      margin: "4px 0 0 0",
                      fontWeight: "600",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {getEmployeeName(selectedEmployeeHistory)} -{" "}
                    {selectedEmployeeHistory.infinityCode}
                  </p>
                </div>
              </div>
              <button
                style={styles.closeButton}
                onClick={() => setShowEmployeeHistoryModal(false)}
                className="enhanced-button-hover"
              >
                <X size={24} />
              </button>
            </div>

            {/* Current Employee Info */}
            <div style={styles.infoBox}>
              <h3
                style={{
                  fontSize: "clamp(15px, 2.5vw, 17px)",
                  fontWeight: "800",
                  marginBottom: "14px",
                  color: theme === "dark" ? "#ffffff" : "#0f172a",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t.currentInfo}
              </h3>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>{t.currentPosition}</span>
                <span style={styles.infoValue}>
                  {selectedEmployeeHistory.employeeType || t.notAvailable}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>{t.currentDepartment}</span>
                <span style={styles.infoValue}>
                  {selectedEmployeeHistory.employeeType || t.notAvailable}
                </span>
              </div>
              <div
                style={{
                  ...styles.infoRow,
                  borderBottom: "none",
                  marginBottom: 0,
                  paddingBottom: 0,
                }}
              >
                <span style={styles.infoLabel}>{t.currentSalary}</span>
                <span style={styles.infoValue}>
                  {selectedEmployeeHistory.netSalary
                    ? `${t.egp
                    } ${selectedEmployeeHistory.netSalary.toLocaleString()}`
                    : t.notAvailable}
                </span>
              </div>
            </div>

            {/* Timeline */}
            <div style={{ marginTop: "24px" }}>
              <h3
                style={{
                  fontSize: "clamp(16px, 2.8vw, 18px)",
                  fontWeight: "800",
                  marginBottom: "20px",
                  color: theme === "dark" ? "#ffffff" : "#0f172a",
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <Activity size={20} />
                {t.actionTimeline} ({employeeHistoryData.length} {t.records})
              </h3>

              {employeeHistoryData.length > 0 ? (
                <div
                  style={{
                    position: "relative",
                    [isRTL ? "paddingRight" : "paddingLeft"]: "40px",
                  }}
                >
                  {/* Timeline Line */}
                  <div
                    style={{
                      position: "absolute",
                      [isRTL ? "right" : "left"]: "16px",
                      top: "20px",
                      bottom: "20px",
                      width: "3px",
                      background:
                        theme === "dark"
                          ? "linear-gradient(180deg, #2e7cba, #06b6d4)"
                          : "linear-gradient(180deg, #60a5fa, #22d3ee)",
                      borderRadius: "10px",
                    }}
                  />

                  {/* Timeline Items */}
                  {employeeHistoryData.map((action, index) => {
                    const actionConfig = ACTION_TYPES.find(
                      (a) => a.id === action.actionType
                    );
                    const ActionIcon = actionConfig?.icon || FileText;
                    const actionDate = new Date(
                      action.effectiveDate || action.createdAt
                    );

                    return (
                      <div
                        key={action.id}
                        style={{
                          position: "relative",
                          marginBottom:
                            index === employeeHistoryData.length - 1
                              ? "0"
                              : "24px",
                        }}
                      >
                        {/* Timeline Dot */}
                        <div
                          style={{
                            position: "absolute",
                            [isRTL ? "right" : "left"]: "-32px",
                            top: "8px",
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            background: actionConfig?.color || "#2e7cba",
                            boxShadow: `0 0 0 4px ${theme === "dark"
                              ? "rgba(30, 41, 59, 1)"
                              : "rgba(255, 255, 255, 1)"
                              }`,
                            zIndex: 1,
                          }}
                        />

                        {/* Timeline Card */}
                        <div
                          style={{
                            background:
                              theme === "dark"
                                ? "rgba(255,255,255,0.06)"
                                : "rgba(255,255,255,0.9)",
                            padding: "16px",
                            borderRadius: "12px",
                            border:
                              theme === "dark"
                                ? "1px solid rgba(255,255,255,0.12)"
                                : "1px solid rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                          }}
                          className="timeline-card-hover"
                        >
                          {/* Date Header */}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: isRTL ? "row" : "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: isRTL ? "row" : "row",
                                alignItems: "center",
                                gap: "8px",
                                color:
                                  theme === "dark"
                                    ? "rgba(255,255,255,0.75)"
                                    : "#64748b",
                                fontSize: "clamp(11px, 1.6vw, 13px)",
                                fontWeight: "700",
                              }}
                            >
                              <Calendar size={14} />
                              {actionDate.toLocaleDateString(
                                language === "ar" ? "ar-EG" : "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                }
                              )}
                            </div>

                            {/* Status Badge */}
                            <div
                              style={{
                                ...styles.badge,
                                background:
                                  action.status === "approved" ||
                                    action.status === "completed"
                                    ? "rgba(16, 185, 129, 0.15)"
                                    : action.status === "pending"
                                      ? "rgba(245, 158, 11, 0.15)"
                                      : "rgba(239, 68, 68, 0.15)",
                                border:
                                  action.status === "approved" ||
                                    action.status === "completed"
                                    ? "1px solid rgba(16, 185, 129, 0.4)"
                                    : action.status === "pending"
                                      ? "1px solid rgba(245, 158, 11, 0.4)"
                                      : "1px solid rgba(239, 68, 68, 0.4)",
                                color:
                                  action.status === "approved" ||
                                    action.status === "completed"
                                    ? theme === "dark"
                                      ? "#34d399"
                                      : "#059669"
                                    : action.status === "pending"
                                      ? theme === "dark"
                                        ? "#fbbf24"
                                        : "#d97706"
                                      : theme === "dark"
                                        ? "#f87171"
                                        : "#dc2626",
                              }}
                            >
                              {action.status === "approved" ||
                                action.status === "completed" ? (
                                <Check size={12} />
                              ) : action.status === "pending" ? (
                                <Clock size={12} />
                              ) : (
                                <X size={12} />
                              )}
                              {t[action.status] || action.status}
                            </div>
                          </div>

                          {/* Action Type */}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: isRTL ? "row" : "row",
                              alignItems: "center",
                              gap: "10px",
                              marginBottom: "12px",
                            }}
                          >
                            <div
                              style={{
                                ...styles.badge,
                                background:
                                  actionConfig?.bgColor ||
                                  "rgba(100, 116, 139, 0.15)",
                                border: `1px solid ${actionConfig?.borderColor ||
                                  "rgba(100, 116, 139, 0.4)"
                                  }`,
                                color: theme === "dark" ? "#ffffff" : "#0f172a",
                              }}
                            >
                              <ActionIcon size={14} />
                              {actionConfig
                                ? t[actionConfig.labelKey]
                                : action.actionType}
                            </div>
                          </div>

                          {/* Changes Grid */}
                          {(action.oldPosition ||
                            action.newPosition ||
                            action.oldSalary ||
                            action.newSalary ||
                            action.oldDepartment ||
                            action.newDepartment) && (
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fit, minmax(200px, 1fr))",
                                  gap: "12px",
                                  marginTop: "12px",
                                  padding: "12px",
                                  background:
                                    theme === "dark"
                                      ? "rgba(0,0,0,0.2)"
                                      : "rgba(0,0,0,0.03)",
                                  borderRadius: "8px",
                                }}
                              >
                                {/* Position Change */}
                                {(action.oldPosition || action.newPosition) && (
                                  <div
                                    style={{
                                      textAlign: isRTL ? "right" : "left",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "clamp(10px, 1.5vw, 11px)",
                                        color:
                                          theme === "dark"
                                            ? "rgba(255,255,255,0.5)"
                                            : "#94a3b8",
                                        marginBottom: "4px",
                                        fontWeight: "700",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                      }}
                                    >
                                      {t.position}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "clamp(12px, 1.7vw, 13px)",
                                        fontWeight: "600",
                                        color:
                                          theme === "dark"
                                            ? "rgba(255,255,255,0.65)"
                                            : "#64748b",
                                      }}
                                    >
                                      {action.oldPosition || t.notAvailable}
                                    </div>
                                    {action.newPosition &&
                                      action.newPosition !==
                                      action.oldPosition && (
                                        <>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: isRTL
                                                ? "flex-end"
                                                : "flex-start",
                                              margin: "4px 0",
                                            }}
                                          >
                                            {isRTL ? (
                                              <ArrowDownRight
                                                size={14}
                                                style={{
                                                  color:
                                                    action.actionType ===
                                                      "promote"
                                                      ? "#10b981"
                                                      : "#ef4444",
                                                  transform: "rotate(180deg)",
                                                }}
                                              />
                                            ) : (
                                              <ArrowDownRight
                                                size={14}
                                                style={{
                                                  color:
                                                    action.actionType ===
                                                      "promote"
                                                      ? "#10b981"
                                                      : "#ef4444",
                                                }}
                                              />
                                            )}
                                          </div>
                                          <div
                                            style={{
                                              fontSize:
                                                "clamp(12px, 1.7vw, 13px)",
                                              fontWeight: "700",
                                              color:
                                                theme === "dark"
                                                  ? "#ffffff"
                                                  : "#0f172a",
                                            }}
                                          >
                                            {action.newPosition}
                                          </div>
                                        </>
                                      )}
                                  </div>
                                )}

                                {/* Salary Change */}
                                {(action.oldSalary || action.newSalary) && (
                                  <div
                                    style={{
                                      textAlign: isRTL ? "right" : "left",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "clamp(10px, 1.5vw, 11px)",
                                        color:
                                          theme === "dark"
                                            ? "rgba(255,255,255,0.5)"
                                            : "#94a3b8",
                                        marginBottom: "4px",
                                        fontWeight: "700",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                      }}
                                    >
                                      {t.salary}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "clamp(12px, 1.7vw, 13px)",
                                        fontWeight: "600",
                                        color:
                                          theme === "dark"
                                            ? "rgba(255,255,255,0.65)"
                                            : "#64748b",
                                      }}
                                    >
                                      {t.egp}{" "}
                                      {(action.oldSalary || 0).toLocaleString()}
                                    </div>
                                    {action.newSalary &&
                                      action.newSalary !== action.oldSalary && (
                                        <>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: isRTL
                                                ? "flex-end"
                                                : "flex-start",
                                              margin: "4px 0",
                                            }}
                                          >
                                            {isRTL ? (
                                              <ArrowDownRight
                                                size={14}
                                                style={{
                                                  color:
                                                    action.newSalary >
                                                      action.oldSalary
                                                      ? "#10b981"
                                                      : "#ef4444",
                                                  transform: "rotate(180deg)",
                                                }}
                                              />
                                            ) : (
                                              <ArrowDownRight
                                                size={14}
                                                style={{
                                                  color:
                                                    action.newSalary >
                                                      action.oldSalary
                                                      ? "#10b981"
                                                      : "#ef4444",
                                                }}
                                              />
                                            )}
                                          </div>
                                          <div
                                            style={{
                                              fontSize:
                                                "clamp(12px, 1.7vw, 13px)",
                                              fontWeight: "700",
                                              color:
                                                action.newSalary >
                                                  action.oldSalary
                                                  ? "#10b981"
                                                  : "#ef4444",
                                            }}
                                          >
                                            {t.egp}{" "}
                                            {action.newSalary.toLocaleString()}
                                            <span
                                              style={{
                                                fontSize:
                                                  "clamp(10px, 1.5vw, 11px)",
                                                [isRTL
                                                  ? "marginRight"
                                                  : "marginLeft"]: "6px",
                                              }}
                                            >
                                              (
                                              {action.newSalary > action.oldSalary
                                                ? "+"
                                                : ""}
                                              {action.changePercent?.toFixed(1)}%)
                                            </span>
                                          </div>
                                        </>
                                      )}
                                  </div>
                                )}

                                {/* Department Change */}
                                {(action.oldDepartment ||
                                  action.newDepartment) && (
                                    <div
                                      style={{
                                        textAlign: isRTL ? "right" : "left",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "clamp(10px, 1.5vw, 11px)",
                                          color:
                                            theme === "dark"
                                              ? "rgba(255,255,255,0.5)"
                                              : "#94a3b8",
                                          marginBottom: "4px",
                                          fontWeight: "700",
                                          textTransform: "uppercase",
                                          letterSpacing: "0.5px",
                                        }}
                                      >
                                        {t.department}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "clamp(12px, 1.7vw, 13px)",
                                          fontWeight: "600",
                                          color:
                                            theme === "dark"
                                              ? "rgba(255,255,255,0.65)"
                                              : "#64748b",
                                        }}
                                      >
                                        {action.oldDepartment || t.notAvailable}
                                      </div>
                                      {action.newDepartment &&
                                        action.newDepartment !==
                                        action.oldDepartment && (
                                          <>
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: isRTL
                                                  ? "flex-end"
                                                  : "flex-start",
                                                margin: "4px 0",
                                              }}
                                            >
                                              {isRTL ? (
                                                <ArrowRight
                                                  size={14}
                                                  style={{
                                                    color: "#2e7cba",
                                                    transform: "rotate(180deg)",
                                                  }}
                                                />
                                              ) : (
                                                <ArrowRight
                                                  size={14}
                                                  style={{ color: "#2e7cba" }}
                                                />
                                              )}
                                            </div>
                                            <div
                                              style={{
                                                fontSize:
                                                  "clamp(12px, 1.7vw, 13px)",
                                                fontWeight: "700",
                                                color:
                                                  theme === "dark"
                                                    ? "#ffffff"
                                                    : "#0f172a",
                                              }}
                                            >
                                              {action.newDepartment}
                                            </div>
                                          </>
                                        )}
                                    </div>
                                  )}
                              </div>
                            )}

                          {/* Reason */}
                          {action.reason && (
                            <div
                              style={{
                                marginTop: "12px",
                                padding: "10px",
                                background:
                                  theme === "dark"
                                    ? "rgba(255,255,255,0.03)"
                                    : "rgba(0,0,0,0.02)",
                                borderRadius: "6px",
                                [isRTL
                                  ? "borderRight"
                                  : "borderLeft"]: `3px solid ${actionConfig?.color || "#2e7cba"
                                  }`,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "clamp(11px, 1.6vw, 12px)",
                                  color:
                                    theme === "dark"
                                      ? "rgba(255,255,255,0.75)"
                                      : "#64748b",
                                  lineHeight: "1.5",
                                  textAlign: isRTL ? "right" : "left",
                                }}
                              >
                                {action.reason}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  <div style={styles.emptyStateIcon}>
                    <History size={60} />
                  </div>
                  <h3 style={styles.emptyStateTitle}>{t.noHistoryFound}</h3>
                  <p style={styles.emptyStateText}>{t.noActionsForEmployee}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {showRejectModal && rejectingActionData && (
        <div
          style={styles.modal}
          onClick={() => {
            setShowRejectModal(false);
            setRejectingActionId(null);
            setRejectingActionData(null);
            setRejectionReason("");
          }}
        >
          <div
            style={{ ...styles.modalContent, maxWidth: "min(90vw, 600px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isRTL ? "row" : "row",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    ...styles.modalIconWrapper,
                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                  }}
                >
                  <X size={26} color="white" />
                </div>
                <div>
                  <h2 style={styles.modalTitle}>{t.reject} {t.employeeAction}</h2>
                  <p
                    style={{
                      fontSize: "clamp(12px, 1.7vw, 14px)",
                      color: theme === "dark" ? "rgba(255,255,255,0.65)" : "#64748b",
                      margin: "4px 0 0 0",
                      fontWeight: "600",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {language === "ar"
                      ? rejectingActionData.employeeArabicName || rejectingActionData.employeeName
                      : rejectingActionData.employeeName || rejectingActionData.employeeArabicName}
                  </p>
                </div>
              </div>
              <button
                style={styles.closeButton}
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingActionId(null);
                  setRejectingActionData(null);
                  setRejectionReason("");
                }}
                className="enhanced-button-hover"
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ ...styles.alertBox, ...styles.warningAlert }}>
              <AlertCircle size={22} />
              <span style={{ fontWeight: "700", fontSize: "clamp(13px, 1.9vw, 15px)" }}>
                {t.provideDetailedReason || "Please provide a detailed reason for rejection"}
              </span>
            </div>

            {/* Action Summary */}
            <div style={styles.infoBox}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>{t.actionType}</span>
                <span style={styles.infoValue}>
                  {(() => {
                    const actionConfig = ACTION_TYPES.find(a => a.id === rejectingActionData.actionType);
                    return actionConfig ? t[actionConfig.labelKey] : rejectingActionData.actionType;
                  })()}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>{t.effectiveDate}</span>
                <span style={styles.infoValue}>
                  {rejectingActionData.effectiveDate
                    ? new Date(rejectingActionData.effectiveDate).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")
                    : t.notAvailable}
                </span>
              </div>
              <div style={{ ...styles.infoRow, borderBottom: "none", marginBottom: 0, paddingBottom: 0 }}>
                <span style={styles.infoLabel}>{t.reason}</span>
                <span style={{ ...styles.infoValue, fontSize: "clamp(12px, 1.7vw, 14px)" }}>
                  {rejectingActionData.reason || t.noDetailsProvided}
                </span>
              </div>
            </div>

            {/* Rejection Reason Input */}
            <div style={styles.controlGroup}>
              <label style={styles.label}>
                <FileText size={14} />
                {t.rejectionReason || "Rejection Reason"} *
              </label>
              <textarea
                name="rejectionReason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                style={styles.textarea}
                className="enhanced-input-focus"
                placeholder={t.provideDetailedReason || "Provide a detailed reason for rejecting this action..."}
                required
                autoFocus
              />
            </div>

            {/* Error Message */}
            {errorMsg && (
              <div style={{ ...styles.alertBox, ...styles.errorAlert }} className="fade-in-alert">
                <AlertCircle size={20} />
                <span>{errorMsg}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                flexDirection: isRTL ? "row" : "row",
                gap: "14px",
                marginTop: "clamp(24px, 3.8vw, 32px)",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                style={{ ...styles.secondaryButton, flex: 1 }}
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingActionId(null);
                  setRejectingActionData(null);
                  setRejectionReason("");
                }}
                className="enhanced-button-hover"
                disabled={isSubmitting}
              >
                <X size={18} /> {t.cancel}
              </button>
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  flex: 1,
                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                }}
                onClick={() => {
                  if (!rejectionReason.trim()) {
                    setErrorMsg(t.reasonRequired || "Rejection reason is required");
                    setTimeout(() => setErrorMsg(""), 3000);
                    return;
                  }
                  handleRejectAction(rejectingActionId, rejectionReason);
                }}
                className="enhanced-button-hover"
                disabled={isSubmitting || !rejectionReason.trim()}
              >
                {isSubmitting ? (
                  <>
                    <Loader size={18} style={styles.loadingSpinner} /> {t.processing}
                  </>
                ) : (
                  <>
                    <X size={18} /> {t.confirmSubmit || "Confirm Rejection"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @keyframes dropdownSlideIn {
            from {
              opacity: 0;
              transform: translateY(-12px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .fade-in-alert {
            animation: fadeIn 0.4s ease;
          }

          .hover-card-header:hover {
            transform: translateY(-4px);
            box-shadow: ${theme === "dark"
            ? "0 30px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.15)"
            : "0 30px 80px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.9)"
          };
          }

          .hover-stat-card:hover {
            transform: translateY(-6px) scale(1.02);
            box-shadow: ${theme === "dark"
            ? "0 18px 55px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)"
            : "0 18px 55px rgba(0,0,0,0.12)"
          };
          }

          .enhanced-input-focus:focus {
            border-color: #2e7cba;
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
            transform: scale(1.01);
          }

          .enhanced-select-focus:focus {
            border-color: #2e7cba;
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
          }

          .enhanced-button-hover:hover {
            transform: translateY(-2px);
            box-shadow: ${theme === "dark"
            ? "0 14px 35px rgba(59, 130, 246, 0.5)"
            : "0 14px 35px rgba(59, 130, 246, 0.4)"
          };
          }

          .enhanced-button-hover:active {
            transform: translateY(0);
          }

          .action-btn-hover:hover {
            transform: scale(1.05);
            box-shadow: 0 10px 30px rgba(59, 130, 246, 0.5);
          }

          .table-row-hover:hover {
            background: ${theme === "dark"
            ? "rgba(59, 130, 246, 0.08)"
            : "rgba(59, 130, 246, 0.05)"
          } !important;
          }

          .dropdown-menu-item-hover:hover {
            background: ${theme === "dark"
            ? "rgba(59, 130, 246, 0.15)"
            : "rgba(59, 130, 246, 0.1)"
          };
            transform: translateX(${isRTL ? "-4px" : "4px"});
          }

          .dropdown-menu-animation {
            animation: dropdownSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          }

          .timeline-card-hover:hover {
            transform: translateX(${isRTL ? "-4px" : "4px"});
            box-shadow: ${theme === "dark"
            ? "0 8px 24px rgba(0,0,0,0.4)"
            : "0 8px 24px rgba(0,0,0,0.1)"
          };
          }

          /* Custom Scrollbar */
          ::-webkit-scrollbar {
            width: 10px;
            height: 10px;
          }

          ::-webkit-scrollbar-track {
            background: ${theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"
          };
            border-radius: 10px;
          }

          ::-webkit-scrollbar-thumb {
            background: ${theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"
          };
            border-radius: 10px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: ${theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"
          };
          }

          /* Hide initials overlay when an image is present (use general sibling and !important) */
          .employeeAvatar img ~ .avatar-initials {
            display: none !important;
          }

          /* RTL Support */
          [dir="rtl"] {
            direction: rtl;
            text-align: right;
          }

          [dir="rtl"] .enhanced-button-hover:hover {
            transform: translateY(-2px);
          }
        `}
      </style>
    </div>
  );
};

export default EmployeeActionsPage;